import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner, Modal, Table, Nav, Pagination } from 'react-bootstrap';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { getCurrentUserRole, getCurrentUserId } from '../../utils/auth';
import { feeSettingsService, FeeSettings, UpdateFeeRequest, AddCustomFeeRequest, FeeFlowType, FlowFeeSettings } from '../../services/feeSettingsService';
import { dashboardService, User } from '../../services/dashboardService';
import { telegramAccountsService } from '../../services/telegramAccountsService';
import { apiKeysService } from '../../services/apiKeysService';
import { embedTokensService } from '../../services/embedTokensService';
import { merchantClientCredentialsService } from '../../services/merchantClientCredentialsService';
import { webhooksService } from '../../services/webhooksService';
import { widgetDistributionService, WidgetDistributionMetadata } from '../../services/widgetDistributionService';
import {
  fiatOnrampApprovalService,
  FiatOnrampApprovalQueueFilter,
  FiatOnrampApprovalQueueItem,
  FiatOnrampApprovalStatus,
  FiatOnrampApprovalStatusInfo,
} from '../../services/fiatOnrampApprovalService';
import { API_CONFIG } from '../../config/env';
import { getRoleDisplayLabel, MERCHANT_LABEL, SUB_MERCHANT_LABEL } from '../../utils/displayLabels';
import { useWindowRefresh } from '../../hooks/useWindowRefresh';
import InternalPageHeader from '../Layout/InternalPageHeader';
import { scheduleScrollPositionReset } from '../Layout/RouteScrollReset';
import { FIAT_CURRENCIES } from '../../constants/fiatCurrencies';
import './Settings.css';

const FEE_FLOW_CONFIG: Array<{ key: FeeFlowType; label: string; description: string }> = [
  {
    key: 'DirectCrypto',
    label: 'Direct Crypto',
    description: 'Use this for crypto payments that settle in USDC on Polygon. Crypto swap payments follow this same fee profile once the funds arrive.'
  },
  {
    key: 'FiatOnramp',
    label: 'Fiat Onramp',
    description: 'Payments completed through embedded fiat providers like Onramper.'
  }
];

type IframeThemePreference = 'dark';

const IFRAME_THEME_PRESETS: Record<IframeThemePreference, { label: string; description: string }> = {
  dark: {
    label: 'RizzPay dark',
    description: 'Matches the current RizzPay hosted payment flow.'
  }
};

interface EditableFee {
  userId: number;
  percent: number;
  isEditing: boolean;
  tempValue: string;
  feeType: 'superAgent' | 'agent';
  flowType: FeeFlowType;
}

type ProcessingFeeDrafts = Record<FeeFlowType, string>;

interface WalletManagerUser extends User {
  canEdit: boolean;
}

const WALLET_USERS_PAGE_SIZE = 10;

const Settings: React.FC = () => {
  const [feeSettings, setFeeSettings] = useState<FeeSettings | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editableFees, setEditableFees] = useState<EditableFee[]>([]);
  const [editingProcessingFlow, setEditingProcessingFlow] = useState<FeeFlowType | null>(null);
  const [processingFeeDrafts, setProcessingFeeDrafts] = useState<ProcessingFeeDrafts>({
    DirectCrypto: '',
    FiatOnramp: ''
  });
  const [showProcessingFeeModal, setShowProcessingFeeModal] = useState(false);
  const [processingFeeAccessGranted, setProcessingFeeAccessGranted] = useState(false);
  const [processingFeePassword, setProcessingFeePassword] = useState('');
  const [processingFeePasswordError, setProcessingFeePasswordError] = useState<string | null>(null);
  const [processingFeeUnlockToken, setProcessingFeeUnlockToken] = useState<string | null>(null);
  const [processingFeeUnlockExpiresAt, setProcessingFeeUnlockExpiresAt] = useState<string | null>(null);
  const [processingFeeUnlocking, setProcessingFeeUnlocking] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newFee, setNewFee] = useState({
    userId: 0,
    feeType: 'SuperAgent' as 'SuperAgent' | 'Agent',
    percent: 0,
    flowType: 'DirectCrypto' as FeeFlowType
  });

  // Telegram Accounts state
  const [telegramAccounts, setTelegramAccounts] = useState<string[]>([]);
  const [telegramLoading, setTelegramLoading] = useState(false);
  const [newTelegramId, setNewTelegramId] = useState('');
  const [showTelegramModal, setShowTelegramModal] = useState(false);

  // API Access state
  const [merchantClientId, setMerchantClientId] = useState<string | null>(null);
  const [merchantClientSecretName, setMerchantClientSecretName] = useState<string | null>(null);
  const [merchantClientRotatedAt, setMerchantClientRotatedAt] = useState<string | null>(null);
  const [merchantClientLastUsedAt, setMerchantClientLastUsedAt] = useState<string | null>(null);
  const [generatedMerchantClientSecret, setGeneratedMerchantClientSecret] = useState('');
  const [merchantCredentialLoading, setMerchantCredentialLoading] = useState(false);
  const [apiKey, setApiKey] = useState<string | null>(null);
  const [apiKeyLoading, setApiKeyLoading] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState<string | null>(null);
  const [webhookLoading, setWebhookLoading] = useState(false);
  const [newWebhookUrl, setNewWebhookUrl] = useState('');
  const [webhookToken, setWebhookToken] = useState<string | null>(null);
  const [webhookTokenName, setWebhookTokenName] = useState<string | null>(null);
  const [webhookTokenRotatedAt, setWebhookTokenRotatedAt] = useState<string | null>(null);
  const [webhookEndpointValidatedAt, setWebhookEndpointValidatedAt] = useState<string | null>(null);
  const [webhookTokenLoading, setWebhookTokenLoading] = useState(false);
  const [embedTokenName, setEmbedTokenName] = useState<string | null>(null);
  const [embedTokenLoading, setEmbedTokenLoading] = useState(false);
  const [generatedEmbedToken, setGeneratedEmbedToken] = useState('');
  const [widgetMetadata, setWidgetMetadata] = useState<WidgetDistributionMetadata | null>(null);
  const [widgetLoading, setWidgetLoading] = useState(false);
  const [iframeTheme, setIframeTheme] = useState<IframeThemePreference>('dark');
  const [iframeLaunchMode, setIframeLaunchMode] = useState<'flexible' | 'shop'>('flexible');
  const [shopIframeAmount, setShopIframeAmount] = useState('149.99');
  const [shopIframeCurrency, setShopIframeCurrency] = useState('USD');
  const [walletUsers, setWalletUsers] = useState<WalletManagerUser[]>([]);
  const [walletUsersLoading, setWalletUsersLoading] = useState(false);
  const [walletUsersPage, setWalletUsersPage] = useState(1);
  const [showWalletModal, setShowWalletModal] = useState(false);
  const [selectedWalletUser, setSelectedWalletUser] = useState<WalletManagerUser | null>(null);
  const [walletAddress, setWalletAddress] = useState('');
  const [walletSaving, setWalletSaving] = useState(false);
  const [fiatOnrampStatus, setFiatOnrampStatus] = useState<FiatOnrampApprovalStatusInfo | null>(null);
  const [fiatOnrampStatusLoading, setFiatOnrampStatusLoading] = useState(false);
  const [fiatOnrampRequestTelegram, setFiatOnrampRequestTelegram] = useState('');
  const [fiatOnrampRequestSubmitting, setFiatOnrampRequestSubmitting] = useState(false);
  const [fiatOnrampQueueFilter, setFiatOnrampQueueFilter] = useState<FiatOnrampApprovalQueueFilter>('Pending');
  const [fiatOnrampQueue, setFiatOnrampQueue] = useState<FiatOnrampApprovalQueueItem[]>([]);
  const [fiatOnrampQueueLoading, setFiatOnrampQueueLoading] = useState(false);
  const [fiatOnrampReviewNotes, setFiatOnrampReviewNotes] = useState<Record<number, string>>({});
  const [fiatOnrampActionByMerchant, setFiatOnrampActionByMerchant] = useState<Record<number, boolean>>({});

  const userRole = getCurrentUserRole();
  const userId = getCurrentUserId();
  const location = useLocation();
  const navigate = useNavigate();
  const numericUserId = userId ? parseInt(userId, 10) : null;
  const iframeThemeStorageKey = `smartech:iframeTheme:${numericUserId ?? userId ?? 'anonymous'}`;

  // Check if user should see tabs (Admin, SuperAgent, and Agent)
  const shouldShowTabs = userRole === 'Admin' || userRole === 'SuperAgent' || userRole === 'Agent';
  const canAccessWidgetWorkspace = shouldShowTabs;
  const telegramFeatureEnabled = API_CONFIG.TELEGRAM_FEATURE_ENABLED;

  const getDefaultTabForRole = useCallback((): string => {
    return userRole === 'Agent' ? 'wallets' : 'fee-customise';
  }, [userRole]);

  const isTabAccessible = useCallback((tabKey: string): boolean => {
    if (!shouldShowTabs) {
      return false;
    }

    if (tabKey === 'fee-customise') {
      return userRole === 'Admin' || userRole === 'SuperAgent';
    }

    if (tabKey === 'wallets') {
      return true;
    }

    if (tabKey === 'telegram-ids') {
      return telegramFeatureEnabled;
    }

    if (tabKey === 'api-access' || tabKey === 'iframe') {
      return userRole === 'SuperAgent' || userRole === 'Agent';
    }

    if (tabKey === 'widget') {
      return canAccessWidgetWorkspace;
    }

    if (tabKey === 'onramper-flow') {
      return userRole === 'Admin' || userRole === 'SuperAgent' || userRole === 'Agent';
    }

    return false;
  }, [canAccessWidgetWorkspace, shouldShowTabs, telegramFeatureEnabled, userRole]);

  const normalizeTabQueryValue = useCallback((tabValue: string | null): string => {
    const rawTab = tabValue?.trim() ?? '';

    if (!rawTab) {
      return getDefaultTabForRole();
    }

    if (rawTab === 'onramper-approvals') {
      return 'onramper-flow';
    }

    if (isTabAccessible(rawTab)) {
      return rawTab;
    }

    return getDefaultTabForRole();
  }, [getDefaultTabForRole, isTabAccessible]);

  // Tab state
  // Agent users start with wallets, Admin and SuperAgent users start with fee-customise
  const [activeTab, setActiveTab] = useState<string>(() =>
    normalizeTabQueryValue(new URLSearchParams(window.location.search).get('tab'))
  );
  const lastBootstrappedTabRef = React.useRef<string | null>(null);
  const previousActiveTabRef = React.useRef(activeTab);

  // Helper function to safely extract error message from API response
  const getErrorMessage = useCallback((err: unknown, defaultMessage: string): string => {
    if (axios.isAxiosError(err) && err.response?.data) {
      const data = err.response.data;
      // If data has a message property, use it
      if (typeof data === 'object' && data !== null && 'message' in data && typeof data.message === 'string') {
        return data.message;
      }
      // If data has a nested error.message property, use it
      if (
        typeof data === 'object' &&
        data !== null &&
        'error' in data &&
        typeof data.error === 'object' &&
        data.error !== null &&
        'message' in data.error &&
        typeof data.error.message === 'string'
      ) {
        return data.error.message;
      }
      // ProblemDetails payloads usually include detail/title
      if (typeof data === 'object' && data !== null && 'detail' in data && typeof data.detail === 'string') {
        return data.detail;
      }
      if (typeof data === 'object' && data !== null && 'title' in data && typeof data.title === 'string') {
        return data.title;
      }
      // If data is a string, use it
      if (typeof data === 'string') {
        return data;
      }
      // If data is an object, try to extract meaningful error info
      if (typeof data === 'object' && data !== null) {
        // Try common error property names
        if ('error' in data && typeof data.error === 'string') {
          return data.error;
        }
        // If it has expandedError, try to extract from it
        if ('expandedError' in data && typeof data.expandedError === 'string') {
          return data.expandedError;
        }
        // Last resort: stringify the object (but this should be rare)
        return JSON.stringify(data);
      }
    }
    return defaultMessage;
  }, []);

  const isTransientApiError = useCallback((err: unknown): boolean => {
    if (!axios.isAxiosError(err)) {
      return false;
    }

    if (!err.response) {
      return true;
    }

    const transientStatusCodes = new Set([408, 425, 429, 500, 502, 503, 504]);
    return transientStatusCodes.has(err.response.status);
  }, []);

  const getRetryAfterDelayMs = useCallback((err: unknown): number | null => {
    if (!axios.isAxiosError(err)) {
      return null;
    }

    const retryAfterRaw = err.response?.headers?.['retry-after'];
    if (!retryAfterRaw) {
      return null;
    }

    const retryAfterValue = Array.isArray(retryAfterRaw) ? retryAfterRaw[0] : String(retryAfterRaw);
    const trimmedValue = retryAfterValue.trim();

    if (!trimmedValue) {
      return null;
    }

    const delaySeconds = Number.parseInt(trimmedValue, 10);
    if (!Number.isNaN(delaySeconds) && delaySeconds >= 0) {
      return delaySeconds * 1000;
    }

    const retryTimestamp = Date.parse(trimmedValue);
    if (Number.isNaN(retryTimestamp)) {
      return null;
    }

    return Math.max(0, retryTimestamp - Date.now());
  }, []);

  const runWithTransientRetry = useCallback(async <T,>(
    operation: () => Promise<T>,
    label: string,
    maxAttempts: number = 4
  ): Promise<T> => {
    let attempt = 0;
    let lastError: unknown;

    while (attempt < maxAttempts) {
      attempt += 1;

      try {
        return await operation();
      } catch (err) {
        lastError = err;

        const canRetry = attempt < maxAttempts && isTransientApiError(err);
        if (!canRetry) {
          throw err;
        }

        const retryAfterDelayMs = getRetryAfterDelayMs(err);
        const exponentialDelayMs = Math.min(8000, 500 * (2 ** (attempt - 1)));
        const jitterMs = Math.floor(Math.random() * 300);
        const delayMs = Math.max(retryAfterDelayMs ?? 0, exponentialDelayMs + jitterMs);

        console.warn(
          `[settings] transient ${label} failure on attempt ${attempt}/${maxAttempts}; retrying in ${delayMs}ms`,
          err
        );

        await new Promise<void>((resolve) => window.setTimeout(resolve, delayMs));
      }
    }

    throw lastError;
  }, [getRetryAfterDelayMs, isTransientApiError]);

  const maskApiKeyValue = useCallback((value: string): string => {
    const trimmedValue = value.trim();
    if (!trimmedValue) {
      return '';
    }

    if (trimmedValue.includes('*')) {
      return trimmedValue;
    }

    return `${trimmedValue.slice(0, 4)}*****`;
  }, []);

  const formatTimestampLabel = useCallback((value: string | null | undefined): string => {
    const trimmedValue = value?.trim();
    if (!trimmedValue) {
      return 'Not available yet';
    }

    const parsedDate = new Date(trimmedValue);
    if (Number.isNaN(parsedDate.getTime())) {
      return trimmedValue;
    }

    return parsedDate.toLocaleString();
  }, []);

  const getFiatOnrampStatusLabel = useCallback((status: FiatOnrampApprovalStatus, enabled: boolean): string => {
    if (enabled || status === 'Approved') {
      return 'Enabled';
    }

    switch (status) {
      case 'Pending':
        return 'Pending review';
      case 'Rejected':
        return 'Rejected';
      case 'Disabled':
        return 'Disabled';
      default:
        return 'Not requested';
    }
  }, []);

  const getFiatOnrampStatusTone = useCallback((status: FiatOnrampApprovalStatus, enabled: boolean): 'ready' | 'action' | 'warning' | 'neutral' => {
    if (enabled || status === 'Approved') {
      return 'ready';
    }

    if (status === 'Pending') {
      return 'action';
    }

    if (status === 'Rejected' || status === 'Disabled') {
      return 'warning';
    }

    return 'neutral';
  }, []);

  const normalizePersistedIframeTheme = useCallback((_value: string | null | undefined): IframeThemePreference => 'dark', []);

  const handleIframeThemeSelect = useCallback((_theme: IframeThemePreference) => {
    setIframeTheme('dark');
  }, []);

  const isValidEthereumAddress = (address: string): boolean => /^0x[a-fA-F0-9]{40}$/.test(address);
  const getAddFeeTypeDisplayLabel = (feeType: 'SuperAgent' | 'Agent') => feeType === 'SuperAgent' ? 'Processing' : `${SUB_MERCHANT_LABEL} Pay`;
  const getAddFeeTargetLabel = (feeType: 'SuperAgent' | 'Agent') => feeType === 'SuperAgent' ? MERCHANT_LABEL.toLowerCase() : SUB_MERCHANT_LABEL.toLowerCase();
  const getFlowLabel = (flowType: FeeFlowType) => FEE_FLOW_CONFIG.find(flow => flow.key === flowType)?.label ?? flowType;
  const getFlowDescription = (flowType: FeeFlowType) => FEE_FLOW_CONFIG.find(flow => flow.key === flowType)?.description ?? '';
  const getFlowSettings = (flowType: FeeFlowType): FlowFeeSettings | null =>
    feeSettings?.flowSettings.find(flow => flow.flowType === flowType) ?? null;

  const getWalletScopeDescription = () => {
    if (userRole === 'Admin') {
      return `Update payout wallets for yourself, all ${MERCHANT_LABEL.toLowerCase()}s, and all ${SUB_MERCHANT_LABEL.toLowerCase()}s. Changes affect future payment requests and invoices only.`;
    }

    if (userRole === 'SuperAgent') {
      return `Update your own payout wallet and the wallets of ${SUB_MERCHANT_LABEL.toLowerCase()}s under your account. Changes affect future payment requests and invoices only.`;
    }

    return 'Update your payout wallet here. Changes affect future payment requests and invoices only.';
  };

  const buildWalletUsers = useCallback((currentUser: User, relatedUsers: User[]): WalletManagerUser[] => {
    const dedupedUsers = Array.from(
      new Map([currentUser, ...relatedUsers].map(user => [user.id, user])).values()
    );

    const scopedUsers = dedupedUsers.filter(user => {
      if (userRole === 'Admin') {
        return true;
      }

      if (userRole === 'SuperAgent') {
        return user.id === currentUser.id || (user.role === 'Agent' && user.createdById === currentUser.id);
      }

      return user.id === currentUser.id;
    });

    return scopedUsers
      .map(user => ({
        ...user,
        canEdit: userRole === 'Admin'
          || user.id === currentUser.id
          || (userRole === 'SuperAgent' && user.role === 'Agent' && user.createdById === currentUser.id)
      }))
      .sort((left, right) => {
        const roleOrder = { Admin: 0, SuperAgent: 1, Agent: 2 };
        const roleDifference = roleOrder[left.role] - roleOrder[right.role];

        if (roleDifference !== 0) {
          return roleDifference;
        }

        return left.name.localeCompare(right.name);
      });
  }, [userRole]);

  const initializeEditableFees = useCallback((settings: FeeSettings) => {
    const fees: EditableFee[] = [];

    settings.flowSettings.forEach(flowSettings => {
      if (userRole === 'Admin') {
        fees.push(
          ...flowSettings.superAgentFees.map(fee => ({
            userId: fee.userId,
            percent: fee.percent,
            isEditing: false,
            tempValue: fee.percent.toString(),
            feeType: 'superAgent' as const,
            flowType: flowSettings.flowType
          })),
          ...flowSettings.agentFees.map(fee => ({
            userId: fee.userId,
            percent: fee.percent,
            isEditing: false,
            tempValue: fee.percent.toString(),
            feeType: 'agent' as const,
            flowType: flowSettings.flowType
          }))
        );
      } else if (userRole === 'SuperAgent') {
        fees.push(
          ...flowSettings.agentFees.map(fee => ({
            userId: fee.userId,
            percent: fee.percent,
            isEditing: false,
            tempValue: fee.percent.toString(),
            feeType: 'agent' as const,
            flowType: flowSettings.flowType
          }))
        );
      }
    });

    setEditableFees(fees);
    setProcessingFeeDrafts({
      DirectCrypto: settings.flowSettings.find(flow => flow.flowType === 'DirectCrypto')?.defaultProcessingPercent.toString() ?? '',
      FiatOnramp: settings.flowSettings.find(flow => flow.flowType === 'FiatOnramp')?.defaultProcessingPercent.toString() ?? ''
    });
    setEditingProcessingFlow(null);
  }, [userRole]);

  const fetchWalletUsers = useCallback(async ({ silent = false }: { silent?: boolean } = {}) => {
    if (!shouldShowTabs) {
      return;
    }

    const showBlockingLoading = !silent;

    try {
      if (showBlockingLoading) {
        setWalletUsersLoading(true);
      }
      const currentUser = await runWithTransientRetry(
        () => dashboardService.getCurrentUser(),
        'wallet current-user fetch'
      );

      const shouldLoadRelatedUsers = userRole === 'Admin' || userRole === 'SuperAgent';
      const relatedUsers = shouldLoadRelatedUsers
        ? users.length > 0
          ? users
          : await runWithTransientRetry(
              () => dashboardService.getUsers(),
              'wallet related-users fetch'
            )
        : [];

      setWalletUsers(buildWalletUsers(currentUser, relatedUsers));
    } catch (err) {
      if (!silent) {
        const errorMessage = getErrorMessage(err, 'Failed to load wallet settings');
        setError(errorMessage);
      }
      console.error('Error fetching wallet users:', err);
    } finally {
      if (showBlockingLoading) {
        setWalletUsersLoading(false);
      }
    }
  }, [buildWalletUsers, getErrorMessage, runWithTransientRetry, shouldShowTabs, userRole, users]);

  const fetchWidgetMetadata = useCallback(async () => {
    try {
      setWidgetLoading(true);
      setError(null);
      const metadata = await widgetDistributionService.getWidgetMetadata();
      setWidgetMetadata(metadata);
    } catch (err) {
      console.error('Error fetching widget distribution metadata:', err);
      setWidgetMetadata(null);
      setError('Failed to load WooCommerce widget distribution metadata');
    } finally {
      setWidgetLoading(false);
    }
  }, []);

  const fetchData = useCallback(async ({ silent = false }: { silent?: boolean } = {}) => {
    const showBlockingLoading = !silent;

    try {
      if (showBlockingLoading) {
        setLoading(true);
      }
      if (!silent) {
        setError(null);
      }

      const [settings, usersData] = await Promise.all([
        runWithTransientRetry(
          () => feeSettingsService.getFeeSettings(),
          'fee-settings bootstrap fetch'
        ),
        runWithTransientRetry(
          () => dashboardService.getUsers(),
          'users bootstrap fetch'
        )
      ]);

      setFeeSettings(settings);
      setUsers(usersData);
      initializeEditableFees(settings);
    } catch (err) {
      if (!silent) {
        const errorMessage = getErrorMessage(
          err,
          'Failed to fetch data. This is often temporary if the API is warming up.'
        );
        setError(errorMessage);
      }
      console.error('Error fetching data:', err);
    } finally {
      if (showBlockingLoading) {
        setLoading(false);
      }
    }
  }, [getErrorMessage, initializeEditableFees, runWithTransientRetry]);

  // Fetch API key (for API Access/Iframe tabs) and webhook (API Access only)
  useEffect(() => {
    if (!shouldShowTabs) {
      return;
    }

    if (lastBootstrappedTabRef.current === activeTab) {
      return;
    }

    lastBootstrappedTabRef.current = activeTab;

    if ((userRole === 'Admin' || userRole === 'SuperAgent') && activeTab === 'fee-customise') {
      void fetchData({ silent: Boolean(feeSettings) });
      return;
    }

    if (activeTab === 'wallets') {
      void fetchWalletUsers({ silent: walletUsers.length > 0 });
      return;
    }

    if (telegramFeatureEnabled && activeTab === 'telegram-ids') {
      fetchTelegramAccounts();
      return;
    }

    if (activeTab === 'onramper-flow') {
      if (userRole === 'Admin') {
        void fetchFiatOnrampQueue(fiatOnrampQueueFilter, { silent: false });
      } else if (userRole === 'SuperAgent' || userRole === 'Agent') {
        void fetchFiatOnrampStatus();
      }
      return;
    }

    if (userRole === 'SuperAgent' || userRole === 'Agent') {
      if (activeTab === 'api-access') {
        fetchMerchantClientCredentials();
        fetchApiKey();
        fetchWebhook();
      } else if (activeTab === 'iframe') {
        fetchEmbedToken();
      }
    }

    if (canAccessWidgetWorkspace && activeTab === 'widget') {
      fetchWidgetMetadata();
    }
  // The tab bootstrap handlers intentionally stay outside this dependency array.
  // They are invoked only after a tab change and can trigger retries/loading state,
  // so binding them here would cause unnecessary re-runs.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeTab,
    canAccessWidgetWorkspace,
    feeSettings,
    fetchData,
    fetchWalletUsers,
    fetchWidgetMetadata,
    fiatOnrampQueueFilter,
    shouldShowTabs,
    telegramFeatureEnabled,
    userRole,
    walletUsers.length,
  ]);

  useEffect(() => {
    if (telegramFeatureEnabled || activeTab !== 'telegram-ids') {
      return;
    }

    setActiveTab(getDefaultTabForRole());
  }, [activeTab, getDefaultTabForRole, telegramFeatureEnabled]);

  useEffect(() => {
    if (!shouldShowTabs) {
      return;
    }

    const tabFromQuery = new URLSearchParams(location.search).get('tab');
    const normalizedTab = normalizeTabQueryValue(tabFromQuery);
    setActiveTab(previousTab => previousTab === normalizedTab ? previousTab : normalizedTab);
  }, [location.search, normalizeTabQueryValue, shouldShowTabs]);

  useEffect(() => {
    if (!shouldShowTabs || isTabAccessible(activeTab)) {
      return;
    }

    setActiveTab(getDefaultTabForRole());
  }, [activeTab, getDefaultTabForRole, isTabAccessible, shouldShowTabs]);

  useEffect(() => {
    if (!shouldShowTabs) {
      return;
    }

    const params = new URLSearchParams(location.search);
    if (params.get('tab') === activeTab) {
      return;
    }

    params.set('tab', activeTab);
    navigate(
      {
        pathname: location.pathname,
        search: `?${params.toString()}`,
      },
      {
        replace: true,
      }
    );
  }, [activeTab, location.pathname, location.search, navigate, shouldShowTabs]);

  React.useLayoutEffect(() => {
    if (previousActiveTabRef.current === activeTab) {
      return undefined;
    }

    previousActiveTabRef.current = activeTab;

    return scheduleScrollPositionReset();
  }, [activeTab]);

  useEffect(() => {
    try {
      const savedTheme = normalizePersistedIframeTheme(localStorage.getItem(iframeThemeStorageKey));

      setIframeTheme(savedTheme);
    } catch (storageError) {
      console.error('Error loading saved iframe colors:', storageError);
      setIframeTheme('dark');
    }
  }, [
    iframeThemeStorageKey,
    normalizePersistedIframeTheme
  ]);

  useEffect(() => {
    try {
      localStorage.setItem(iframeThemeStorageKey, iframeTheme);
    } catch (storageError) {
      console.error('Error saving iframe theme:', storageError);
    }
  }, [iframeTheme, iframeThemeStorageKey]);

  const getUserName = (userId: number): string => {
    const user = users.find(u => u.id === userId);
    return user ? user.name : `User ${userId}`;
  };

  const handleEditFee = (userId: number, feeType: 'superAgent' | 'agent', flowType: FeeFlowType) => {
    setEditableFees(prev =>
      prev.map(fee =>
        fee.userId === userId && fee.feeType === feeType && fee.flowType === flowType
          ? { ...fee, isEditing: true, tempValue: fee.percent.toString() }
          : fee
      )
    );
  };

  const handleCancelEdit = (userId: number, feeType: 'superAgent' | 'agent', flowType: FeeFlowType) => {
    setEditableFees(prev =>
      prev.map(fee =>
        fee.userId === userId && fee.feeType === feeType && fee.flowType === flowType
          ? { ...fee, isEditing: false, tempValue: fee.percent.toString() }
          : fee
      )
    );
  };

  const handleSaveFee = async (userId: number, feeType: 'superAgent' | 'agent', flowType: FeeFlowType) => {
    const editableFee = editableFees.find(fee =>
      fee.userId === userId && fee.feeType === feeType && fee.flowType === flowType
    );
    if (!editableFee) return;

    const newPercent = parseFloat(editableFee.tempValue);
    if (isNaN(newPercent) || newPercent < 0 || newPercent > 100) {
      setError('Please enter a valid percentage between 0 and 100');
      return;
    }

    try {
      setError(null);
      setSuccess(null);

      const updateRequest: UpdateFeeRequest = {
        type: feeType === 'superAgent' ? 'SuperAgent' : 'Agent',
        targetId: userId,
        percent: newPercent,
        flowType
      };

      const response = await feeSettingsService.updateFeeSettings(updateRequest);

      if (response.updated) {
        // Update local state
        setFeeSettings(prev => {
          if (!prev) return prev;

          return {
            ...prev,
            flowSettings: prev.flowSettings.map(flow =>
              flow.flowType !== flowType
                ? flow
                : {
                    ...flow,
                    superAgentFees: feeType === 'superAgent'
                      ? flow.superAgentFees.some(fee => fee.userId === userId)
                        ? flow.superAgentFees.map(fee => fee.userId === userId ? { ...fee, percent: newPercent } : fee)
                        : [...flow.superAgentFees, { userId, percent: newPercent }]
                      : flow.superAgentFees,
                    agentFees: feeType === 'agent'
                      ? flow.agentFees.some(fee => fee.userId === userId)
                        ? flow.agentFees.map(fee => fee.userId === userId ? { ...fee, percent: newPercent } : fee)
                        : [...flow.agentFees, { userId, percent: newPercent }]
                      : flow.agentFees
                  }
            )
          };
        });

        setEditableFees(prev =>
          prev.map(fee =>
            fee.userId === userId && fee.feeType === feeType && fee.flowType === flowType
              ? { ...fee, isEditing: false, percent: newPercent, tempValue: newPercent.toString() }
              : fee
          )
        );

        setSuccess(`${getFlowLabel(flowType)} fee updated successfully`);
      } else {
        setError('Failed to update fee - server returned false');
      }
    } catch (err) {
      setError('Failed to update fee');
      console.error('Error updating fee:', err);
    }
  };

  const handleStartProcessingFeeEdit = (flowType: FeeFlowType, currentPercent: number) => {
    setProcessingFeeDrafts(prev => ({
      ...prev,
      [flowType]: currentPercent.toString()
    }));
    setEditingProcessingFlow(flowType);
  };

  const handleCancelProcessingFeeEdit = (flowType: FeeFlowType, currentPercent: number) => {
    setProcessingFeeDrafts(prev => ({
      ...prev,
      [flowType]: currentPercent.toString()
    }));
    if (editingProcessingFlow === flowType) {
      setEditingProcessingFlow(null);
    }
  };

  const handleProcessingFeeDraftChange = (flowType: FeeFlowType, value: string) => {
    setProcessingFeeDrafts(prev => ({
      ...prev,
      [flowType]: value
    }));
  };

  const handleSaveProcessingFee = async (flowType: FeeFlowType) => {
    const draftValue = processingFeeDrafts[flowType];
    const newPercent = parseFloat(draftValue);
    if (Number.isNaN(newPercent) || newPercent < 0 || newPercent > 100) {
      setError('Please enter a valid percentage between 0 and 100');
      return;
    }

    if (!processingFeeUnlockToken || !processingFeeUnlockExpiresAt || Date.parse(processingFeeUnlockExpiresAt) <= Date.now()) {
      setProcessingFeeAccessGranted(false);
      setProcessingFeeUnlockToken(null);
      setProcessingFeeUnlockExpiresAt(null);
      setEditingProcessingFlow(null);
      setError('PF unlock expired. Unlock the hidden fee panel again before saving changes.');
      return;
    }

    try {
      setError(null);
      setSuccess(null);

      const updateRequest: UpdateFeeRequest = {
        type: 'Company',
        targetId: null,
        percent: newPercent,
        flowType
      };

      const response = await feeSettingsService.updateFeeSettings(updateRequest, processingFeeUnlockToken);
      if (!response.updated) {
        setError('Failed to update platform processing fee');
        return;
      }

      setFeeSettings(prev => {
        if (!prev) {
          return prev;
        }

        return {
          ...prev,
          flowSettings: prev.flowSettings.map(flow =>
            flow.flowType === flowType
              ? { ...flow, defaultProcessingPercent: newPercent }
              : flow
          )
        };
      });

      setProcessingFeeDrafts(prev => ({
        ...prev,
        [flowType]: newPercent.toString()
      }));
      setEditingProcessingFlow(null);
      setSuccess(`${getFlowLabel(flowType)} platform processing fee updated successfully`);
    } catch (err) {
      if (axios.isAxiosError(err) && (err.response?.status === 401 || err.response?.status === 403)) {
        setProcessingFeeAccessGranted(false);
        setProcessingFeeUnlockToken(null);
        setProcessingFeeUnlockExpiresAt(null);
        setEditingProcessingFlow(null);
        setProcessingFeePassword('');
        setProcessingFeePasswordError('PF unlock expired. Enter the password again.');
        setError('PF unlock expired. Enter the password again before changing the platform fee.');
      } else {
        setError('Failed to update platform processing fee');
      }
      console.error('Error updating processing fee:', err);
    }
  };

  const handleOpenProcessingFeePanel = () => {
    if (processingFeeUnlockExpiresAt && Date.parse(processingFeeUnlockExpiresAt) <= Date.now()) {
      setProcessingFeeAccessGranted(false);
      setProcessingFeeUnlockToken(null);
      setProcessingFeeUnlockExpiresAt(null);
      setEditingProcessingFlow(null);
    }
    setProcessingFeePasswordError(null);
    setProcessingFeePassword('');
    setShowProcessingFeeModal(true);
  };

  const handleUnlockProcessingFeePanel = async () => {
    if (!processingFeePassword.trim()) {
      setProcessingFeePasswordError('Enter the password');
      return;
    }

    try {
      setProcessingFeeUnlocking(true);
      setProcessingFeePasswordError(null);
      const response = await feeSettingsService.unlockProcessingFeePanel(processingFeePassword);

      setProcessingFeeAccessGranted(true);
      setProcessingFeeUnlockToken(response.unlockToken);
      setProcessingFeeUnlockExpiresAt(response.expiresAtUtc);
      setProcessingFeePassword('');
      setEditingProcessingFlow(null);
    } catch (err) {
      setProcessingFeeAccessGranted(false);
      setProcessingFeeUnlockToken(null);
      setProcessingFeeUnlockExpiresAt(null);
      setProcessingFeePasswordError(
        getErrorMessage(
          err,
          'Incorrect password'
        )
      );
    } finally {
      setProcessingFeeUnlocking(false);
    }
  };

  const handleCloseProcessingFeePanel = () => {
    setShowProcessingFeeModal(false);
    setProcessingFeePassword('');
    setProcessingFeePasswordError(null);
  };

  const handleLockProcessingFeePanel = () => {
    setProcessingFeeAccessGranted(false);
    setProcessingFeeUnlockToken(null);
    setProcessingFeeUnlockExpiresAt(null);
    setEditingProcessingFlow(null);
    setProcessingFeePassword('');
    setProcessingFeePasswordError(null);
    setShowProcessingFeeModal(false);
  };

  const handleAddCustomFee = async () => {
    const availableUsers = users.filter(user => user.role === newFee.feeType);

    if (availableUsers.length === 0) {
      setError(`No ${getAddFeeTargetLabel(newFee.feeType)} users available to assign custom fees`);
      return;
    }

    if (newFee.userId === 0 || newFee.percent <= 0 || newFee.percent > 100) {
      setError('Please select a user and enter a valid percentage');
      return;
    }

    try {
      setError(null);
      setSuccess(null);

      const addRequest: AddCustomFeeRequest = {
        userId: newFee.userId,
        feeType: newFee.feeType,
        percent: newFee.percent,
        flowType: newFee.flowType
      };

      const response = await feeSettingsService.addCustomFee(addRequest);

      if (response.updated) {
        // Refresh data to get updated fees
        await fetchData();
        setShowAddModal(false);
        setNewFee({ userId: 0, feeType: 'SuperAgent', percent: 0, flowType: 'DirectCrypto' });
        setSuccess(`${getFlowLabel(addRequest.flowType)} custom fee added successfully`);
      } else {
        setError('Failed to add custom fee - server returned false');
      }
    } catch (err) {
      setError('Failed to add custom fee');
      console.error('Error adding custom fee:', err);
    }
  };

  const handleTempValueChange = (userId: number, feeType: 'superAgent' | 'agent', flowType: FeeFlowType, value: string) => {
    setEditableFees(prev =>
      prev.map(fee =>
        fee.userId === userId && fee.feeType === feeType && fee.flowType === flowType
          ? { ...fee, tempValue: value }
          : fee
      )
    );
  };

  const handleOpenAddModal = (feeType: 'SuperAgent' | 'Agent', flowType: FeeFlowType) => {
    setNewFee({
      userId: 0,
      feeType: feeType,
      percent: 0,
      flowType
    });
    setShowAddModal(true);
  };

  // Telegram Accounts functions
  const fetchTelegramAccounts = async () => {
    try {
      setTelegramLoading(true);
      const accounts = await telegramAccountsService.getTelegramAccounts();
      setTelegramAccounts(accounts);
    } catch (err) {
      console.error('Error fetching telegram accounts:', err);
      // Don't set error state for telegram accounts as it's not critical
    } finally {
      setTelegramLoading(false);
    }
  };

  const handleAddTelegramAccount = async () => {
    if (!newTelegramId.trim()) {
      setError('Please enter a Telegram ID');
      return;
    }

    // Basic validation for Telegram ID (should be numeric)
    if (!/^\d+$/.test(newTelegramId.trim())) {
      setError('Telegram ID must be numeric');
      return;
    }

    try {
      setError(null);
      setSuccess(null);

      await telegramAccountsService.addTelegramAccount(newTelegramId.trim());

      // If we get here, it means 200 OK was returned
      setSuccess('Telegram account added successfully');
      setNewTelegramId('');
      setShowTelegramModal(false);
      // Refresh the list
      await fetchTelegramAccounts();
    } catch (err) {
      // Error means non-200 status code was returned
      const errorMessage = getErrorMessage(err, 'Failed to add Telegram account');
      setError(errorMessage);
      console.error('Error adding telegram account:', err);
    }
  };

  const handleDeleteTelegramAccount = async (telegramId: string) => {
    if (!window.confirm('Are you sure you want to delete this Telegram account?')) {
      return;
    }

    try {
      setError(null);
      setSuccess(null);

      await telegramAccountsService.deleteTelegramAccount(telegramId);

      // If we get here, it means 200 OK was returned
      setSuccess('Telegram account deleted successfully');
      // Refresh the list
      await fetchTelegramAccounts();
    } catch (err) {
      // Error means non-200 status code was returned
      const errorMessage = getErrorMessage(err, 'Failed to delete Telegram account');
      setError(errorMessage);
      console.error('Error deleting telegram account:', err);
    }
  };

  const handleOpenTgUserId = () => {
    window.open('https://tg-user.id/', '_blank', 'noopener,noreferrer');
  };

  const copyToClipboard = async (value: string, successMessage?: string) => {
    try {
      await navigator.clipboard.writeText(value);
      if (successMessage) {
        setSuccess(successMessage);
      }
    } catch (err) {
      console.error('Clipboard copy failed:', err);
      setError('Failed to copy to clipboard');
    }
  };

  const handleOpenWalletModal = (user: WalletManagerUser) => {
    setSelectedWalletUser(user);
    setWalletAddress(user.settlementWallet || '');
    setShowWalletModal(true);
  };

  const handleCloseWalletModal = () => {
    setShowWalletModal(false);
    setSelectedWalletUser(null);
    setWalletAddress('');
    setWalletSaving(false);
  };

  const handleUpdateWallet = async () => {
    if (!selectedWalletUser) {
      return;
    }

    const trimmedWallet = walletAddress.trim();

    if (!trimmedWallet) {
      setError('Please enter a wallet address');
      return;
    }

    if (!isValidEthereumAddress(trimmedWallet)) {
      setError('Please enter a valid ERC-20 wallet address');
      return;
    }

    try {
      setWalletSaving(true);
      setError(null);
      setSuccess(null);

      await dashboardService.updateUserWallet(selectedWalletUser.id, trimmedWallet);

      setWalletUsers(prev =>
        prev.map(user =>
          user.id === selectedWalletUser.id
            ? { ...user, settlementWallet: trimmedWallet, usdtWallet: trimmedWallet }
            : user
        )
      );

      setUsers(prev =>
        prev.map(user =>
          user.id === selectedWalletUser.id
            ? { ...user, settlementWallet: trimmedWallet, usdtWallet: trimmedWallet }
            : user
        )
      );

      setSuccess('Wallet address updated successfully. Future payment requests and invoices will use the new settlement wallet.');
      handleCloseWalletModal();
    } catch (err) {
      const errorMessage = getErrorMessage(err, 'Failed to update wallet address');
      setError(errorMessage);
      console.error('Error updating wallet address:', err);
    } finally {
      setWalletSaving(false);
    }
  };

  // API Keys functions
  const fetchMerchantClientCredentials = async () => {
    try {
      setMerchantCredentialLoading(true);
      setError(null);
      const credentials = await merchantClientCredentialsService.getCredentials();
      setMerchantClientId(credentials.clientId);
      setMerchantClientSecretName(credentials.clientSecretName);
      setMerchantClientRotatedAt(credentials.rotatedAtUtc ?? null);
      setMerchantClientLastUsedAt(credentials.lastUsedAtUtc ?? null);
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 404) {
        setMerchantClientId(null);
        setMerchantClientSecretName(null);
        setMerchantClientRotatedAt(null);
        setMerchantClientLastUsedAt(null);
      } else {
        console.error('Error fetching merchant client credentials:', err);
        setMerchantClientId(null);
        setMerchantClientSecretName(null);
        setMerchantClientRotatedAt(null);
        setMerchantClientLastUsedAt(null);
      }
    } finally {
      setMerchantCredentialLoading(false);
    }
  };

  const fetchApiKey = async (skipLoading = false) => {
    try {
      if (!skipLoading) {
        setApiKeyLoading(true);
      }
      setError(null);
      const key = await apiKeysService.getApiKey();
      // Only set the key if it's a non-empty string
      if (key && typeof key === 'string' && key.trim().length > 0) {
        setApiKey(key);
      } else {
        // Empty or invalid key - treat as no key exists
        setApiKey(null);
      }
    } catch (err) {
      if (axios.isAxiosError(err)) {
        if (err.response?.status === 404) {
          // No API key exists yet
          setApiKey(null);
        } else {
          // Other errors - log but don't set error state to avoid blocking UI
          // But still set apiKey to null to show "No API key generated yet"
          console.error('Error fetching API key:', err);
          console.error('Response status:', err.response?.status);
          console.error('Response data:', err.response?.data);
          setApiKey(null);
        }
      } else {
        console.error('Error fetching API key:', err);
        setApiKey(null);
      }
    } finally {
      if (!skipLoading) {
        setApiKeyLoading(false);
      }
    }
  };

  const fetchEmbedToken = async () => {
    try {
      setEmbedTokenLoading(true);
      setError(null);
      const name = await embedTokensService.getEmbedTokenName();
      setEmbedTokenName(name && name.trim().length > 0 ? name : null);
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 404) {
        setEmbedTokenName(null);
      } else {
        console.error('Error fetching embed token:', err);
        setEmbedTokenName(null);
      }
    } finally {
      setEmbedTokenLoading(false);
    }
  };

  const fetchFiatOnrampStatus = useCallback(async ({ silent = false }: { silent?: boolean } = {}) => {
    try {
      if (!silent) {
        setFiatOnrampStatusLoading(true);
      }

      const status = await fiatOnrampApprovalService.getMyStatus();
      setFiatOnrampStatus(status);
      if (status.fiatOnrampApprovalTelegramUsername) {
        setFiatOnrampRequestTelegram(status.fiatOnrampApprovalTelegramUsername);
      }
    } catch (err) {
      console.error('Error fetching fiat onramp approval status:', err);
      if (!silent) {
        const errorMessage = getErrorMessage(err, 'Failed to load fiat onramp approval status');
        setError(errorMessage);
      }
      setFiatOnrampStatus(null);
    } finally {
      if (!silent) {
        setFiatOnrampStatusLoading(false);
      }
    }
  }, [getErrorMessage]);

  const fetchFiatOnrampQueue = useCallback(async (
    filter: FiatOnrampApprovalQueueFilter = fiatOnrampQueueFilter,
    { silent = false }: { silent?: boolean } = {}
  ) => {
    try {
      if (!silent) {
        setFiatOnrampQueueLoading(true);
      }

      const effectiveStatus = filter === 'All' ? null : filter;
      const items = await fiatOnrampApprovalService.getRequests(effectiveStatus);
      setFiatOnrampQueue(items);
    } catch (err) {
      console.error('Error fetching fiat onramp approval queue:', err);
      if (!silent) {
        const errorMessage = getErrorMessage(err, 'Failed to load fiat onramp approval queue');
        setError(errorMessage);
      }
      setFiatOnrampQueue([]);
    } finally {
      if (!silent) {
        setFiatOnrampQueueLoading(false);
      }
    }
  }, [fiatOnrampQueueFilter, getErrorMessage]);

  const handleGenerateMerchantClientCredentials = async () => {
    if (!window.confirm('Generate new merchant server credentials? This will replace the current client secret.')) {
      return;
    }

    try {
      setError(null);
      setSuccess(null);
      setMerchantCredentialLoading(true);

      const credentials = await merchantClientCredentialsService.generateCredentials();
      const trimmedSecret = credentials.clientSecret.trim();
      setMerchantClientId(credentials.clientId.trim());
      setGeneratedMerchantClientSecret(trimmedSecret);
      setMerchantClientSecretName(trimmedSecret ? `${trimmedSecret.slice(0, 8)}*****` : null);
      setMerchantClientRotatedAt(new Date().toISOString());
      setMerchantClientLastUsedAt(null);
      setSuccess('Merchant server credentials generated. Copy the client secret now because the full secret is shown only in this session.');
    } catch (err) {
      const errorMessage = getErrorMessage(err, 'Failed to generate merchant server credentials');
      setError(errorMessage);
      console.error('Error generating merchant server credentials:', err);
    } finally {
      setMerchantCredentialLoading(false);
    }
  };

  const handleDeleteMerchantClientCredentials = async () => {
    if (!window.confirm('Delete the current merchant server credentials? Existing server integrations using them will stop working.')) {
      return;
    }

    try {
      setError(null);
      setSuccess(null);
      setMerchantCredentialLoading(true);

      await merchantClientCredentialsService.deleteCredentials();
      setMerchantClientId(null);
      setMerchantClientSecretName(null);
      setMerchantClientRotatedAt(null);
      setMerchantClientLastUsedAt(null);
      setGeneratedMerchantClientSecret('');
      setSuccess('Merchant server credentials deleted successfully');
    } catch (err) {
      const errorMessage = getErrorMessage(err, 'Failed to delete merchant server credentials');
      setError(errorMessage);
      console.error('Error deleting merchant server credentials:', err);
    } finally {
      setMerchantCredentialLoading(false);
    }
  };

  const handleGenerateApiKey = async () => {
    if (!window.confirm('Are you sure you want to generate a new API key? This will replace your existing key.')) {
      return;
    }

    try {
      setError(null);
      setSuccess(null);
      setApiKeyLoading(true);

      // Generate the API key - POST returns {"token":"..."} with full uncensored key
      const newKey = await apiKeysService.generateApiKey();
      setApiKey(newKey);

      setSuccess('API key generated successfully');
    } catch (err) {
      const errorMessage = getErrorMessage(err, 'Failed to generate API key');
      setError(errorMessage);
      console.error('Error generating API key:', err);
    } finally {
      setApiKeyLoading(false);
    }
  };

  const handleDeleteApiKey = async () => {
    if (!window.confirm('Are you sure you want to delete your API key? This action cannot be undone.')) {
      return;
    }

    try {
      setError(null);
      setSuccess(null);
      setApiKeyLoading(true);

      await apiKeysService.deleteApiKey();
      setApiKey(null);
      setSuccess('API key deleted successfully');
    } catch (err) {
      const errorMessage = getErrorMessage(err, 'Failed to delete API key');
      setError(errorMessage);
      console.error('Error deleting API key:', err);
    } finally {
      setApiKeyLoading(false);
    }
  };

  const handleGenerateEmbedToken = async () => {
    if (!window.confirm('Generate a new embed token? This will replace the current iframe token.')) {
      return;
    }

    try {
      setError(null);
      setSuccess(null);
      setEmbedTokenLoading(true);

      const token = await embedTokensService.generateEmbedToken();
      const trimmedToken = token.trim();
      setGeneratedEmbedToken(trimmedToken);
      setEmbedTokenName(trimmedToken ? maskApiKeyValue(trimmedToken) : null);

      setSuccess('Embed token generated. Copy the URL or iframe snippet now because the full token is shown only in this session.');
    } catch (err) {
      const errorMessage = getErrorMessage(err, 'Failed to generate embed token');
      setError(errorMessage);
      console.error('Error generating embed token:', err);
    } finally {
      setEmbedTokenLoading(false);
    }
  };

  const handleDeleteEmbedToken = async () => {
    if (!window.confirm('Delete the current embed token? Any existing iframe snippet using it will stop working.')) {
      return;
    }

    try {
      setError(null);
      setSuccess(null);
      setEmbedTokenLoading(true);

      await embedTokensService.deleteEmbedToken();
      setEmbedTokenName(null);
      setGeneratedEmbedToken('');

      setSuccess('Embed token deleted successfully');
    } catch (err) {
      const errorMessage = getErrorMessage(err, 'Failed to delete embed token');
      setError(errorMessage);
      console.error('Error deleting embed token:', err);
    } finally {
      setEmbedTokenLoading(false);
    }
  };

  // Webhooks functions
  const fetchWebhook = async () => {
    try {
      setWebhookLoading(true);
      const settings = await webhooksService.getWebhook();
      setWebhookUrl(settings.endpoint);
      setWebhookTokenName(settings.tokenName);
      setWebhookTokenRotatedAt(settings.tokenRotatedAtUtc);
      setWebhookEndpointValidatedAt(settings.endpointValidatedAtUtc);
    } catch (err) {
      console.error('Error fetching webhook:', err);
      setWebhookUrl(null);
      setWebhookTokenName(null);
      setWebhookTokenRotatedAt(null);
      setWebhookEndpointValidatedAt(null);
    } finally {
      setWebhookLoading(false);
    }
  };

  const handleSetWebhook = async () => {
    if (!newWebhookUrl.trim()) {
      setError('Please enter a webhook URL');
      return;
    }

    // Basic URL validation
    try {
      new URL(newWebhookUrl.trim());
    } catch {
      setError('Please enter a valid URL');
      return;
    }

    try {
      setError(null);
      setSuccess(null);
      setWebhookLoading(true);

      await webhooksService.setWebhook(newWebhookUrl.trim());
      setWebhookUrl(newWebhookUrl.trim());
      setWebhookEndpointValidatedAt(new Date().toISOString());
      setNewWebhookUrl('');
      setSuccess('Webhook URL set successfully');
    } catch (err) {
      const errorMessage = getErrorMessage(err, 'Failed to set webhook URL');
      setError(errorMessage);
      console.error('Error setting webhook:', err);
    } finally {
      setWebhookLoading(false);
    }
  };

  const handleDeleteWebhook = async () => {
    if (!window.confirm('Are you sure you want to delete your webhook URL?')) {
      return;
    }

    try {
      setError(null);
      setSuccess(null);
      setWebhookLoading(true);

      await webhooksService.deleteWebhook();
      setWebhookUrl(null);
      setWebhookEndpointValidatedAt(null);
      setSuccess('Webhook URL deleted successfully');
    } catch (err) {
      const errorMessage = getErrorMessage(err, 'Failed to delete webhook URL');
      setError(errorMessage);
      console.error('Error deleting webhook:', err);
    } finally {
      setWebhookLoading(false);
    }
  };

  const handleGenerateWebhookToken = async () => {
    try {
      setError(null);
      setSuccess(null);
      setWebhookTokenLoading(true);

      const token = await webhooksService.generateWebhookToken();
      setWebhookToken(token);
      setWebhookTokenName(maskApiKeyValue(token));
      setWebhookTokenRotatedAt(new Date().toISOString());
      setWebhookEndpointValidatedAt(null);
      setSuccess('Webhook token generated successfully');
    } catch (err) {
      const errorMessage = getErrorMessage(err, 'Failed to generate webhook token');
      setError(errorMessage);
      console.error('Error generating webhook token:', err);
    } finally {
      setWebhookTokenLoading(false);
    }
  };

  const handleSubmitFiatOnrampRequest = async () => {
    const telegramUsername = fiatOnrampRequestTelegram.trim();
    if (!telegramUsername) {
      setError('Telegram username is required to request fiat onramp access.');
      return;
    }

    try {
      setError(null);
      setSuccess(null);
      setFiatOnrampRequestSubmitting(true);

      const status = await fiatOnrampApprovalService.submitMyRequest(telegramUsername);
      setFiatOnrampStatus(status);
      setFiatOnrampRequestTelegram(status.fiatOnrampApprovalTelegramUsername || telegramUsername);
      setSuccess('Fiat onramp approval request submitted. The admin team will contact you on Telegram.');
    } catch (err) {
      const errorMessage = getErrorMessage(err, 'Failed to submit fiat onramp approval request');
      setError(errorMessage);
      console.error('Error submitting fiat onramp approval request:', err);
    } finally {
      setFiatOnrampRequestSubmitting(false);
    }
  };

  const handleFiatOnrampQueueFilterChange = (nextFilter: FiatOnrampApprovalQueueFilter) => {
    if (nextFilter === fiatOnrampQueueFilter) {
      return;
    }

    setFiatOnrampQueueFilter(nextFilter);
    void fetchFiatOnrampQueue(nextFilter);
  };

  const handleSetFiatOnrampMerchantState = async (merchant: FiatOnrampApprovalQueueItem, enabled: boolean) => {
    const reviewNote = fiatOnrampReviewNotes[merchant.merchantUserId]?.trim() ?? '';

    try {
      setError(null);
      setSuccess(null);
      setFiatOnrampActionByMerchant((prev) => ({
        ...prev,
        [merchant.merchantUserId]: true,
      }));

      await fiatOnrampApprovalService.setMerchantState(
        merchant.merchantUserId,
        enabled,
        reviewNote || null
      );

      await fetchFiatOnrampQueue(fiatOnrampQueueFilter, { silent: true });
      setSuccess(
        enabled
          ? `Fiat onramp enabled for ${merchant.merchantName || `merchant #${merchant.merchantUserId}`}.`
          : `Fiat onramp disabled for ${merchant.merchantName || `merchant #${merchant.merchantUserId}`}.`
      );
    } catch (err) {
      const errorMessage = getErrorMessage(err, 'Failed to update fiat onramp approval state');
      setError(errorMessage);
      console.error('Error updating fiat onramp approval state:', err);
    } finally {
      setFiatOnrampActionByMerchant((prev) => {
        const next = { ...prev };
        delete next[merchant.merchantUserId];
        return next;
      });
    }
  };

  const refreshActiveTab = async () => {
    if (!shouldShowTabs) {
      return;
    }

    if ((userRole === 'Admin' || userRole === 'SuperAgent') && activeTab === 'fee-customise') {
      await fetchData({ silent: true });
      return;
    }

    if (activeTab === 'wallets') {
      await fetchWalletUsers({ silent: true });
      return;
    }

    if (telegramFeatureEnabled && activeTab === 'telegram-ids') {
      await fetchTelegramAccounts();
      return;
    }

    if (activeTab === 'onramper-flow') {
      if (userRole === 'Admin') {
        await fetchFiatOnrampQueue(fiatOnrampQueueFilter, { silent: true });
      } else if (userRole === 'SuperAgent' || userRole === 'Agent') {
        await fetchFiatOnrampStatus({ silent: true });
      }
      return;
    }

    if (userRole === 'SuperAgent' || userRole === 'Agent') {
      if (activeTab === 'api-access') {
        await Promise.all([
          fetchMerchantClientCredentials(),
          fetchApiKey(),
          fetchWebhook(),
        ]);
      } else if (activeTab === 'iframe') {
        await fetchEmbedToken();
      }
      return;
    }

    if (canAccessWidgetWorkspace && activeTab === 'widget') {
      await fetchWidgetMetadata();
    }
  };

  useWindowRefresh(refreshActiveTab, {
    enabled: shouldShowTabs,
    intervalMs: 90000,
    refreshOnFocus: false,
    refreshOnVisible: false,
  });

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(walletUsers.length / WALLET_USERS_PAGE_SIZE));
    setWalletUsersPage((previous) => Math.min(previous, totalPages));
  }, [walletUsers.length]);

  const handleTabSelect = (tabKey: string | null) => {
    if (!tabKey || tabKey === activeTab) {
      return;
    }

    if (!isTabAccessible(tabKey)) {
      return;
    }

    setError(null);
    setSuccess(null);
    setActiveTab(tabKey);
  };

  // Render Fee Customise tab content (for Admin and SuperAgent)
  const renderFeeCustomiseTab = () => {
    if (!feeSettings) {
      return (
        <Card className="operator-card">
          <Card.Body className="text-center">
            <Spinner animation="border" role="status" className="app-spinner">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </Card.Body>
        </Card>
      );
    }

    return (
      <>
        <Alert variant="info" className="settings-callout">
          Fees are configured per payment flow. Direct crypto settings also apply to crypto swap payments after the funds arrive as USDC on Polygon, so you only need to manage one crypto fee profile here.
        </Alert>

        {FEE_FLOW_CONFIG.map(flow => {
          const flowSettings = getFlowSettings(flow.key);
          if (!flowSettings) {
            return null;
          }

          return (
            <Card key={flow.key} className="operator-card mb-4">
              <Card.Header className="operator-card__header">
                <div className="d-flex flex-column gap-1">
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="operator-card__title">{flow.label}</h5>
                    <span className="operator-muted-text">{flow.key}</span>
                  </div>
                  <small className="operator-card__subtitle">{flow.description}</small>
                </div>
              </Card.Header>
              <Card.Body>
                {userRole === 'Admin' ? (
                  <>
                    <Row className="mb-1">
                      <Col lg={6}>
                        {renderReadOnlyFeeCard(`Default ${MERCHANT_LABEL} Share`, flowSettings.defaultSuperAgentPercent)}
                      </Col>
                      <Col lg={6}>
                        {renderReadOnlyFeeCard(`Default ${SUB_MERCHANT_LABEL} Share`, flowSettings.defaultAgentPercent)}
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        {renderCustomFeesTable(
                          editableFees.filter(fee => fee.feeType === 'superAgent' && fee.flowType === flow.key),
                          'superAgent',
                          flow.key,
                          `${MERCHANT_LABEL} Share Overrides for ${flow.label}`
                        )}
                      </Col>
                      <Col md={6}>
                      {renderCustomFeesTable(
                        editableFees.filter(fee => fee.feeType === 'agent' && fee.flowType === flow.key),
                        'agent',
                        flow.key,
                        `${SUB_MERCHANT_LABEL} Share Overrides for ${flow.label}`
                      )}
                      </Col>
                    </Row>
                  </>
                ) : userRole === 'SuperAgent' ? (
                  <Row>
                    <Col>
                      {renderReadOnlyFeeCard(`Default ${SUB_MERCHANT_LABEL} Share`, flowSettings.defaultAgentPercent)}
                      {renderCustomFeesTable(
                        editableFees.filter(fee => fee.feeType === 'agent' && fee.flowType === flow.key),
                        'agent',
                        flow.key,
                        `${SUB_MERCHANT_LABEL} Share Overrides for ${flow.label}`
                      )}
                    </Col>
                  </Row>
                ) : null}
              </Card.Body>
            </Card>
          );
        })}
      </>
    );
  };

  const renderWalletsTab = () => {
    const managedTargetsCount = walletUsers.filter(user => user.id !== numericUserId).length;
    const walletReadyCount = walletUsers.filter(user => Boolean(user.settlementWallet?.trim())).length;
    const walletNeedsAttentionCount = walletUsers.filter(user => !user.settlementWallet?.trim()).length;
    const totalPages = Math.max(1, Math.ceil(walletUsers.length / WALLET_USERS_PAGE_SIZE));
    const currentPage = Math.min(walletUsersPage, totalPages);
    const currentPageStartIndex = (currentPage - 1) * WALLET_USERS_PAGE_SIZE;
    const currentPageEndIndex = currentPageStartIndex + WALLET_USERS_PAGE_SIZE;
    const visibleWalletUsers = walletUsers.slice(currentPageStartIndex, currentPageEndIndex);

    const renderWalletPagination = () => {
      if (totalPages <= 1) {
        return null;
      }

      const items = [];
      const maxVisiblePages = 5;
      let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
      let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

      if (endPage - startPage + 1 < maxVisiblePages) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
      }

      for (let page = startPage; page <= endPage; page += 1) {
        items.push(
          <Pagination.Item
            key={page}
            active={page === currentPage}
            onClick={() => setWalletUsersPage(page)}
          >
            {page}
          </Pagination.Item>,
        );
      }

      return (
        <Pagination className="app-pagination app-pagination--operator justify-content-center flex-wrap mt-3">
          <Pagination.Prev
            disabled={currentPage === 1}
            onClick={() => currentPage > 1 && setWalletUsersPage(currentPage - 1)}
          />
          {items}
          <Pagination.Next
            disabled={currentPage === totalPages}
            onClick={() => currentPage < totalPages && setWalletUsersPage(currentPage + 1)}
          />
        </Pagination>
      );
    };

    return (
      <div className="settings-wallets">
        <Alert variant="info" className="settings-callout settings-wallets__callout">
          {getWalletScopeDescription()}
        </Alert>

        <div className="operator-summary-grid settings-wallets__summary-grid">
          <div className="operator-summary-card" style={{ '--summary-accent': 'var(--app-blue)' } as React.CSSProperties}>
            <span className="operator-summary-card__label">Scope</span>
            <strong className="operator-summary-card__value">
              {managedTargetsCount > 0 ? `${managedTargetsCount + 1} accounts` : 'Your account'}
            </strong>
            <span className="operator-summary-card__note">
              {managedTargetsCount > 0
                ? `Managing ${managedTargetsCount} linked account${managedTargetsCount === 1 ? '' : 's'} plus your own.`
                : 'This wallet is used for your future payment requests and invoices.'}
            </span>
          </div>
          <div className="operator-summary-card" style={{ '--summary-accent': '#7dd3a4' } as React.CSSProperties}>
            <span className="operator-summary-card__label">Wallets Ready</span>
            <strong className="operator-summary-card__value">{walletReadyCount}</strong>
            <span className="operator-summary-card__note">
              Accounts that already have a Polygon settlement wallet.
            </span>
          </div>
          <div className="operator-summary-card" style={{ '--summary-accent': walletNeedsAttentionCount > 0 ? '#f2b56b' : 'var(--app-text-muted)' } as React.CSSProperties}>
            <span className="operator-summary-card__label">Needs Attention</span>
            <strong className="operator-summary-card__value">{walletNeedsAttentionCount}</strong>
            <span className="operator-summary-card__note">
              {walletNeedsAttentionCount > 0
                ? 'Set these before new payment requests or invoices go live.'
                : 'Every visible account already has a wallet.'}
            </span>
          </div>
        </div>

        <Card className="operator-card settings-wallets__workspace">
          <Card.Header className="operator-card__header d-flex justify-content-between align-items-center">
            <div>
              <h5 className="operator-card__title">Settlement Wallets</h5>
              <small className="operator-card__subtitle">Polygon settlement wallets used for future payout routing.</small>
            </div>
            <Button
              variant="outline-light"
              size="sm"
              onClick={() => void fetchWalletUsers()}
              disabled={walletUsersLoading}
            >
              Refresh
            </Button>
          </Card.Header>
          <Card.Body>
            {walletUsersLoading ? (
              <div className="d-flex justify-content-center">
                <Spinner animation="border" role="status" className="app-spinner">
                  <span className="visually-hidden">Loading...</span>
                </Spinner>
              </div>
            ) : walletUsers.length === 0 ? (
              <p className="settings-empty">No wallet targets available for this account.</p>
            ) : (
              <>
                <div className="settings-wallets__list-meta">
                  <span>
                    Showing {currentPageStartIndex + 1}-{Math.min(currentPageEndIndex, walletUsers.length)} of {walletUsers.length} wallets
                  </span>
                  <span>
                    Page {currentPage} of {totalPages}
                  </span>
                </div>
                <div className="settings-wallets__list">
                {visibleWalletUsers.map(user => {
                  const roleLabel = user.role === 'Admin' ? 'Processor' : getRoleDisplayLabel(user.role);
                  const scopeLabel = user.id === numericUserId
                    ? 'Your account'
                    : userRole === 'Admin'
                      ? 'Managed by processor'
                      : 'Managed under your hierarchy';
                  const hasWallet = Boolean(user.settlementWallet?.trim());

                  return (
                    <section
                      key={user.id}
                      className={`settings-wallets__account ${user.id === numericUserId ? 'settings-wallets__account--current' : ''} ${!hasWallet ? 'settings-wallets__account--attention' : ''}`}
                    >
                      <div className="settings-wallets__account-top">
                        <div className="settings-wallets__identity">
                          <span className="settings-wallets__eyebrow">
                            {user.id === numericUserId ? 'Primary workspace' : 'Managed target'}
                          </span>
                          <h6 className="settings-wallets__title">{user.name}</h6>
                          <p className="settings-wallets__subtitle">{user.email}</p>
                        </div>
                        <div className="settings-wallets__badge-row">
                          <span className="settings-wallets__badge settings-wallets__badge--role">{roleLabel}</span>
                          <span className="settings-wallets__badge settings-wallets__badge--scope">{scopeLabel}</span>
                        </div>
                      </div>

                      <div className="settings-wallets__metrics">
                        <div className={`settings-wallets__metric ${hasWallet ? '' : 'settings-wallets__metric--attention'}`}>
                          <span className="settings-wallets__metric-label">Settlement wallet</span>
                          {hasWallet ? (
                            <strong className="settings-mono-value settings-wallets__wallet-value">{user.settlementWallet}</strong>
                          ) : (
                            <strong className="settings-wallets__metric-value">No wallet set</strong>
                          )}
                          <span className="settings-wallets__metric-copy">
                            {hasWallet
                              ? 'Future payment requests and invoices created for this account will settle here.'
                              : 'Set a Polygon wallet before creating new payment requests or invoices for this account.'}
                          </span>
                        </div>

                        <div className="settings-wallets__metric">
                          <span className="settings-wallets__metric-label">Coverage</span>
                          <strong className="settings-wallets__metric-value">
                            {user.id === numericUserId ? 'Direct control' : 'Managed routing'}
                          </strong>
                          <span className="settings-wallets__metric-copy">
                            {user.canEdit
                              ? 'Update this wallet here in Settings.'
                              : 'Shown here for reference only.'}
                          </span>
                        </div>

                        <div className="settings-wallets__metric settings-wallets__metric--action">
                          <span className="settings-wallets__metric-label">Next action</span>
                          <Button
                            variant={hasWallet ? 'outline-primary' : 'primary'}
                            size="sm"
                            onClick={() => handleOpenWalletModal(user)}
                            disabled={!user.canEdit}
                            className="settings-wallets__action-button"
                          >
                            {hasWallet ? 'Update Wallet' : 'Add Wallet'}
                          </Button>
                          <span className="settings-wallets__metric-copy">
                            {hasWallet
                              ? 'Change this when payout routing needs to move.'
                              : 'Add a wallet before this account goes live.'}
                          </span>
                        </div>
                      </div>
                    </section>
                  );
                })}
                </div>
                {renderWalletPagination()}
              </>
            )}
          </Card.Body>
        </Card>
      </div>
    );
  };

  // Render Telegram IDs tab content
  const renderTelegramIdsTab = () => (
    <Row>
      <Col>
        <Card className="operator-card">
          <Card.Header className="operator-card__header d-flex justify-content-between align-items-center">
            <h5 className="operator-card__title">Telegram Accounts</h5>
            <div>
              <Button
                variant="outline-info"
                size="sm"
                onClick={handleOpenTgUserId}
                className="me-2"
              >
                Get Telegram ID from username
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => setShowTelegramModal(true)}
              >
                Add Account
              </Button>
            </div>
          </Card.Header>
          <Card.Body>
            {telegramLoading ? (
              <div className="d-flex justify-content-center">
                <Spinner animation="border" role="status" className="app-spinner">
                  <span className="visually-hidden">Loading...</span>
                </Spinner>
              </div>
            ) : telegramAccounts.length === 0 ? (
              <p className="settings-empty">No Telegram accounts added yet</p>
            ) : (
              <Table responsive className="app-data-table settings-table">
                <thead>
                  <tr>
                    <th>Telegram ID</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {telegramAccounts.map((telegramId) => (
                    <tr key={telegramId}>
                      <td>
                        <span className="fw-bold settings-mono-value">{telegramId}</span>
                      </td>
                      <td>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDeleteTelegramAccount(telegramId)}
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );

  // Render API Access tab content
  const renderApiAccessTab = () => {
    const normalizedApiBaseUrl = API_CONFIG.BASE_URL.startsWith('http')
      ? API_CONFIG.BASE_URL.replace(/\/+$/, '')
      : `${window.location.origin}${API_CONFIG.BASE_URL}`.replace(/\/+$/, '');
    const merchantCredentialEndpoint = `${normalizedApiBaseUrl}/merchant-client-credentials`;
    const merchantTokenEndpoint = `${normalizedApiBaseUrl}/merchant-auth/token`;
    const createDepositEndpoint = `${normalizedApiBaseUrl}/merchant/deposits`;
    const depositSnapshotEndpoint = `${normalizedApiBaseUrl}/merchant/deposits/payment-id/{paymentId}`;
    const depositEventsEndpoint = `${normalizedApiBaseUrl}/merchant/deposits/payment-id/{paymentId}/events`;
    const effectiveMerchantClientId = merchantClientId?.trim() ?? '';
    const effectiveMerchantClientSecret = generatedMerchantClientSecret.trim();
    const hasMerchantCredentials = effectiveMerchantClientId.length > 0;
    const hasGeneratedMerchantClientSecret = effectiveMerchantClientSecret.length > 0;
    const hasWebhookSecret = Boolean(webhookTokenName || webhookToken);
    const hasWebhookEndpoint = Boolean(webhookUrl);
    const merchantDocsBasePath = `${process.env.PUBLIC_URL || ''}/merchant-integration`;
    const merchantDocsVersion = '2026-05-27';
    const merchantDoc = (name: string, extension: string) =>
      `${merchantDocsBasePath}/${name}_${merchantDocsVersion}.${extension}`;
    const startHereResources = [
      { label: 'Download Current Integration Pack', href: merchantDoc('MERCHANT_INTEGRATION_PACK', 'zip'), variant: 'primary' },
      { label: 'Open Integration Summary', href: merchantDoc('MERCHANT_INTEGRATION_ONE_PAGER', 'md'), variant: 'outline-primary' },
      { label: 'Open Getting Started Guide', href: merchantDoc('MERCHANT_DEVELOPER_GETTING_STARTED', 'md'), variant: 'outline-primary' },
    ];
    const apiToolResources = [
      { label: 'Open API Quick Start', href: merchantDoc('MERCHANT_DEPOSIT_QUICKSTART', 'md'), variant: 'outline-secondary' },
      { label: 'Open API Contract Note', href: merchantDoc('MERCHANT_DEPOSIT_API_NOTE', 'md'), variant: 'outline-secondary' },
      { label: 'Download Postman Collection', href: merchantDoc('MERCHANT_DEPOSIT_POSTMAN_COLLECTION', 'postman_collection.json'), variant: 'outline-secondary' },
      { label: 'Download Postman Environment', href: merchantDoc('MERCHANT_DEPOSIT_POSTMAN_ENVIRONMENT', 'postman_environment.json'), variant: 'outline-secondary' },
    ];
    const webhookResources = [
      { label: 'Open Webhook Samples', href: merchantDoc('MERCHANT_WEBHOOK_SAMPLE_PAYLOADS', 'md'), variant: 'outline-secondary' },
      { label: 'Open Webhook Troubleshooting', href: merchantDoc('MERCHANT_WEBHOOK_TROUBLESHOOTING', 'md'), variant: 'outline-secondary' },
      { label: 'Open CRM Field Mapping', href: merchantDoc('MERCHANT_CRM_FIELD_MAPPING', 'md'), variant: 'outline-secondary' },
      { label: 'Open Go-Live Checklist', href: merchantDoc('MERCHANT_GO_LIVE_CHECKLIST', 'md'), variant: 'outline-secondary' },
    ];
    const starterResources = [
      { label: 'Open Node Receiver Starter', href: `${merchantDocsBasePath}/examples/merchant-webhook-receiver-node/README.md`, variant: 'outline-secondary' },
      { label: 'Open .NET Receiver Starter', href: `${merchantDocsBasePath}/examples/merchant-webhook-receiver-dotnet/README.md`, variant: 'outline-secondary' },
    ];
    const renderResourceLinks = (resources: Array<{ label: string; href: string; variant: string }>) => (
      <div className="d-flex flex-wrap gap-2">
        {resources.map(resource => (
          <a
            key={resource.href}
            href={resource.href}
            target="_blank"
            rel="noreferrer"
            className={`btn btn-sm btn-${resource.variant}`}
          >
            {resource.label}
          </a>
        ))}
      </div>
    );
    const overviewCards = [
      {
        title: '1. Secure your server',
        copy: 'Generate client credentials here, store them in your backend, and exchange them for short-lived bearer tokens.',
        tone: 'primary'
      },
      {
        title: '2. Create hosted deposits',
        copy: 'Use paymentId-first merchant deposit endpoints to return checkoutUrl, wallet details, and card or bank options.',
        tone: 'neutral'
      },
      {
        title: '3. Confirm by webhook',
        copy: 'Treat signed webhooks as your primary signal and keep polling only for recovery or reconciliation.',
        tone: 'neutral'
      },
      {
        title: '4. Keep browser auth separate',
        copy: 'Iframe and embedded browser flows use a dedicated embed token in the Iframe tab, not your full server secret.',
        tone: 'neutral'
      }
    ] as const;
    const readinessCards = [
      {
        title: 'Server auth',
        value: hasMerchantCredentials ? 'Ready' : 'Needs setup',
        copy: hasMerchantCredentials
          ? 'Client credentials already exist for server token exchange.'
          : 'Generate client credentials before wiring your backend.',
        tone: hasMerchantCredentials ? 'ready' : 'action'
      },
      {
        title: 'Webhook secret',
        value: hasWebhookSecret ? 'Ready' : 'Missing',
        copy: hasWebhookSecret
          ? 'A signing secret exists for your receiver.'
          : 'Generate a webhook secret before you set the endpoint.',
        tone: hasWebhookSecret ? 'ready' : 'action'
      },
      {
        title: 'Webhook endpoint',
        value: hasWebhookEndpoint ? 'Configured' : 'Pending',
        copy: hasWebhookEndpoint
          ? 'The receiver URL is stored and can be rotated here.'
          : 'Set your receiver URL after the signing secret is in place.',
        tone: hasWebhookEndpoint ? 'ready' : 'neutral'
      }
    ] as const;
    const serverCredentialSteps = [
      'Generate client credentials once and store them in your backend.',
      'Exchange them for a short-lived bearer token from your server.',
      'Use bearer auth for deposit creation and paymentId lookups.'
    ] as const;
    const depositFlowSteps = [
      'Create the hosted deposit with amount, currency, and merchantReferences.',
      'RizzPay selects the best available card/bank route automatically.',
      'Use signed webhooks first, then poll only for recovery.'
    ] as const;
    const renderExpandableSection = (
      title: string,
      description: string,
      body: React.ReactNode,
      defaultOpen = false
    ) => (
      <details className="settings-api-access__details" open={defaultOpen}>
        <summary className="settings-api-access__details-summary">
          <span className="settings-api-access__details-heading">{title}</span>
          <span className="settings-api-access__details-copy">{description}</span>
        </summary>
        <div className="settings-api-access__details-body">
          {body}
        </div>
      </details>
    );

    return (
      <div className="settings-api-access">
        <Card className="operator-card mb-4 settings-api-access__overview">
          <Card.Body>
            <div className="settings-api-access__eyebrow">Self-serve integration path</div>
            <div className="settings-api-access__hero">
              <div className="settings-api-access__hero-copy">
                <h5 className="operator-card__title mb-2">Set up server auth, hosted deposits, and webhooks from one place</h5>
                <p className="operator-muted-text mb-0">
                  This tab is for server-to-server merchant integrations. Browser embeds stay on the separate Iframe tab on purpose, so your full server secret never leaks into the browser.
                </p>
              </div>
              <div className="settings-api-access__pill-row">
                <span className="settings-api-access__pill">Bearer-first</span>
                <span className="settings-api-access__pill">paymentId-first</span>
                <span className="settings-api-access__pill">Signed webhooks</span>
                <span className="settings-api-access__pill">Self-serve</span>
              </div>
            </div>
            <div className="settings-api-access__overview-grid">
              {overviewCards.map(card => (
                <div
                  key={card.title}
                  className={`settings-api-access__overview-card settings-api-access__overview-card--${card.tone}`}
                >
                  <div className="settings-api-access__overview-title">{card.title}</div>
                  <div className="settings-api-access__overview-copy">{card.copy}</div>
                </div>
              ))}
            </div>
            <div className="settings-api-access__status-grid">
              {readinessCards.map(card => (
                <div
                  key={card.title}
                  className={`settings-api-access__status-card settings-api-access__status-card--${card.tone}`}
                >
                  <div className="settings-api-access__status-label">{card.title}</div>
                  <div className="settings-api-access__status-value">{card.value}</div>
                  <div className="settings-api-access__status-copy">{card.copy}</div>
                </div>
              ))}
            </div>
            <div className="settings-api-access__quick-actions">
              <Button
                variant="primary"
                onClick={handleGenerateMerchantClientCredentials}
                disabled={merchantCredentialLoading}
              >
                {hasMerchantCredentials ? 'Rotate server credentials' : 'Generate server credentials'}
              </Button>
              <a
                href={merchantDoc('MERCHANT_INTEGRATION_PACK', 'zip')}
                target="_blank"
                rel="noreferrer"
                className="btn btn-outline-primary"
              >
                Download current pack
              </a>
              <a
                href={merchantDoc('MERCHANT_DEPOSIT_QUICKSTART', 'md')}
                target="_blank"
                rel="noreferrer"
                className="btn btn-outline-secondary"
              >
                Open API quick start
              </a>
            </div>
          </Card.Body>
        </Card>

        <Row className="g-4">
          <Col xl={8}>
          {/* Recommended Server Credentials Section */}
          <Card className="operator-card mb-4">
            <Card.Header className="operator-card__header">
              <h5 className="operator-card__title">Server API Credentials (Recommended)</h5>
              <small className="operator-card__subtitle">Use this path for any backend integration that creates hosted deposits or checks payment state.</small>
            </Card.Header>
            <Card.Body>
              <div className="settings-guide-grid mb-4">
                {serverCredentialSteps.map((step, index) => (
                  <div key={step} className="settings-guide-card">
                    <div className="settings-guide-card__step">Step {index + 1}</div>
                    <div className="settings-guide-card__copy">{step}</div>
                  </div>
                ))}
              </div>

              {merchantCredentialLoading ? (
                <div className="d-flex justify-content-center">
                  <Spinner animation="border" role="status" className="app-spinner">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                </div>
              ) : (
                <>
                  <div className="mb-3">
                    <Form.Label>Client ID</Form.Label>
                    {hasMerchantCredentials ? (
                      <div className="d-flex align-items-center">
                        <Form.Control
                          type="text"
                          value={effectiveMerchantClientId}
                          readOnly
                          className="me-2 settings-code-field"
                        />
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => copyToClipboard(effectiveMerchantClientId, 'Client ID copied to clipboard')}
                        >
                          Copy
                        </Button>
                      </div>
                    ) : (
                      <p className="operator-muted-text mb-0">No merchant client credentials generated yet.</p>
                    )}
                  </div>

                  <div className="mb-3">
                    <Form.Label>Stored Client Secret</Form.Label>
                    {merchantClientSecretName ? (
                      <Form.Control
                        type="text"
                        value={merchantClientSecretName}
                        readOnly
                        className="settings-code-field"
                      />
                    ) : (
                      <p className="operator-muted-text mb-0">No client secret stored yet.</p>
                    )}
                    <Form.Text>
                      The stored value is masked on purpose. Generate a new secret whenever you need to rotate it.
                    </Form.Text>
                  </div>

                  <div className="mb-3">
                    <Form.Label>Credential Activity</Form.Label>
                    <div className="settings-code-block">
                      <div className="settings-code-block__line">
                        Last rotated: {formatTimestampLabel(merchantClientRotatedAt)}
                      </div>
                      <div className="settings-code-block__line">
                        Last used: {formatTimestampLabel(merchantClientLastUsedAt)}
                      </div>
                    </div>
                    <Form.Text>
                      Use this to confirm when your server credentials were last rotated and whether they are still being used.
                    </Form.Text>
                  </div>

                  <div className="mb-3">
                    <Form.Label>New Client Secret (shown once)</Form.Label>
                    {hasGeneratedMerchantClientSecret ? (
                      <div className="d-flex align-items-center">
                        <Form.Control
                          type="text"
                          value={effectiveMerchantClientSecret}
                          readOnly
                          className="me-2 settings-code-field"
                          style={{ '--code-color': '#7dd3a4' } as React.CSSProperties}
                        />
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => copyToClipboard(effectiveMerchantClientSecret, 'Client secret copied to clipboard')}
                        >
                          Copy
                        </Button>
                      </div>
                    ) : (
                      <p className="operator-muted-text mb-0">
                        Generate credentials to reveal the full client secret for this session. Store it in your backend only.
                      </p>
                    )}
                  </div>

                  <div className="d-flex flex-wrap gap-2">
                    <Button
                      variant="primary"
                      onClick={handleGenerateMerchantClientCredentials}
                      disabled={merchantCredentialLoading}
                    >
                      {hasMerchantCredentials ? 'Regenerate Credentials' : 'Generate Credentials'}
                    </Button>
                    <Button
                      variant="outline-danger"
                      onClick={handleDeleteMerchantClientCredentials}
                      disabled={merchantCredentialLoading || !hasMerchantCredentials}
                    >
                      Delete Credentials
                    </Button>
                  </div>
                </>
              )}
            </Card.Body>
          </Card>
          </Col>
          <Col xl={4}>

          {/* Legacy API Key Section */}
          <Card className="operator-card mb-4">
            <Card.Header className="operator-card__header">
              <h5 className="operator-card__title">Legacy API Key (Fallback)</h5>
              <small className="operator-card__subtitle">Keep this only for older server integrations that have not moved to bearer auth yet.</small>
            </Card.Header>
            <Card.Body>
              <div className="settings-callout settings-callout--warning mb-3">
                Keep this only while older server integrations finish moving to bearer auth.
              </div>
              {apiKeyLoading ? (
                <div className="d-flex justify-content-center">
                  <Spinner animation="border" role="status" className="app-spinner">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                </div>
              ) : apiKey ? (
                <div className="mb-3">
                  <Form.Label>Your API Key</Form.Label>
                  <div className="d-flex align-items-center">
                    <Form.Control
                      type="text"
                      value={apiKey}
                      readOnly
                      className="me-2 settings-code-field"
                    />
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={handleDeleteApiKey}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              ) : (
                <p className="operator-muted-text mb-3">No API key generated yet</p>
              )}
              <Button
                variant="primary"
                onClick={handleGenerateApiKey}
                disabled={apiKeyLoading}
              >
                {apiKey ? 'Regenerate API Key' : 'Generate API Key'}
              </Button>
            </Card.Body>
          </Card>
          </Col>
        </Row>

          {/* Merchant Deposit API Section */}
          <Card className="operator-card mb-4">
            <Card.Header className="operator-card__header">
              <h5 className="operator-card__title">Merchant Deposit API</h5>
              <small className="operator-card__subtitle">Create hosted deposit sessions and track them with paymentId-first lookup routes.</small>
            </Card.Header>
            <Card.Body>
              <div className="settings-guide-grid mb-4">
                {depositFlowSteps.map((step, index) => (
                  <div key={step} className="settings-guide-card">
                    <div className="settings-guide-card__step">Flow {index + 1}</div>
                    <div className="settings-guide-card__copy">{step}</div>
                  </div>
                ))}
              </div>
              <div className="mb-3">
                <Form.Label>Preferred Endpoints</Form.Label>
                <div className="settings-code-block">
                  <div className="settings-code-block__line">POST {createDepositEndpoint}</div>
                  <div className="settings-code-block__line">GET {depositSnapshotEndpoint}</div>
                  <div className="settings-code-block__line">GET {depositEventsEndpoint}</div>
                </div>
              </div>
              <div className="mb-3">
                <Form.Label>Required Headers</Form.Label>
                <div className="settings-code-block">
                  <div className="settings-code-block__line">
                    Content-Type: application/json
                  </div>
                  <div className="settings-code-block__line">
                    Authorization: Bearer &lt;merchant access token&gt;
                  </div>
                </div>
                <Form.Text>
                  Legacy support: <code>X-COR-ApiKey</code> still works during migration, but bearer auth is the preferred path for new integrations.
                </Form.Text>
              </div>
              <div className="mb-3">
                <Form.Label>Available User IDs</Form.Label>
                <div className="settings-code-block">
                  <p className="mb-2">
                    <strong>Your userId:</strong>{' '}
                    <span className="settings-mono-value">{userId || 'Unavailable'}</span>
                  </p>
                  {userRole === 'SuperAgent' && numericUserId !== null ? (
                    <>
                      {users.some(u => u.role === 'Agent' && u.createdById === numericUserId) ? (
                        <>
                          <p className="operator-muted-text mb-2">
                      {SUB_MERCHANT_LABEL}s you can reference:
                          </p>
                          <ul className="mb-0 ps-3">
                            {users
                              .filter(u => u.role === 'Agent' && u.createdById === numericUserId)
                              .map(agent => (
                                <li key={agent.id}>
                              {agent.name || SUB_MERCHANT_LABEL} - userId: <span className="settings-mono-value">{agent.id}</span>
                                </li>
                              ))}
                          </ul>
                        </>
                      ) : (
                        <p className="operator-muted-text mb-0">
                    You currently have no linked {SUB_MERCHANT_LABEL}s. Use your own userId.
                        </p>
                      )}
                    </>
                  ) : (
                    <p className="operator-muted-text mb-0">
                      Use your own userId when creating hosted deposits.
                    </p>
                  )}
                </div>
                <Form.Text>
                  <code>userId</code> is the internal beneficiary account on this platform. Keep your own player, session, and order identifiers inside <code>merchantReferences</code>.
                </Form.Text>
              </div>
              <div className="settings-api-access__details-stack">
                {renderExpandableSection(
                  'Create hosted deposit',
                  'Request body with merchantReferences for reconciliation and payer continuity.',
                  <Form.Control
                    as="textarea"
                    rows={10}
                    value={JSON.stringify({
                      userId: Number(userId || 0),
                      fiatCurrency: "USD",
                      amount: 100,
                      merchantReferences: {
                        merchantTransactionId: "deposit-20260330-001",
                        merchantUserId: "player-4421",
                        merchantSessionId: "session-abc-77",
                        idempotencyKey: "deposit-20260330-001-attempt-1",
                        metadata: {
                          brand: "main-casino",
                          channel: "web"
                        }
                      }
                    }, null, 2)}
                    readOnly
                    className="settings-code-field"
                    style={{ '--code-color': '#7dd3a4' } as React.CSSProperties}
                  />
                )}
                {renderExpandableSection(
                  'Read the response shape',
                  'paymentId is the main merchant-facing identifier. depositId remains only as a compatibility field during migration.',
                  <Form.Control
                    as="textarea"
                    rows={18}
                    value={JSON.stringify({
                      paymentId: "abc123",
                      depositId: 0,
                      status: "Issued",
                      displayStatus: "Issued",
                      checkoutUrl: "https://pay.your-domain.com/pay/abc123",
                      publicPaymentId: "abc123",
                      requestedAmount: {
                        amount: 100,
                        currency: "USD"
                      },
                      walletPayment: {
                        asset: "USDC",
                        network: "Polygon",
                        walletAddress: "0x123...",
                        quotedAmount: null,
                        quoteExpiresAt: null
                      },
                      cardOrBankOptions: [
                        {
                          checkoutSessionId: "session-1",
                          providerKey: "onramper:moonpay",
                          provider: "moonpay",
                          paymentMethod: "creditcard",
                          url: "https://...",
                          recommended: true
                        }
                      ],
                      merchantReferences: {
                        merchantTransactionId: "deposit-20260330-001",
                        merchantUserId: "player-4421",
                        merchantSessionId: "session-abc-77",
                        idempotencyKey: "deposit-20260330-001-attempt-1"
                      }
                    }, null, 2)}
                    readOnly
                    className="settings-code-field"
                    style={{ '--code-color': '#7dd3a4' } as React.CSSProperties}
                  />
                )}
              </div>
              <div className="operator-muted-text">
                Use webhooks as the primary notification path. Poll the <code>GET /api/merchant/deposits/{'{paymentId}'}</code> endpoints for recovery, reconciliation, or support tooling.
              </div>
            </Card.Body>
          </Card>

          <Card className="operator-card mb-4">
            <Card.Header className="operator-card__header">
              <h5 className="operator-card__title">Server Token Exchange</h5>
              <small className="operator-card__subtitle">Your backend exchanges the stored clientId and clientSecret for a short-lived access token.</small>
            </Card.Header>
            <Card.Body>
              <div className="mb-3">
                <Form.Label>Credential Management Endpoint</Form.Label>
                <div className="settings-code-block">
                  <div className="settings-code-block__line">GET {merchantCredentialEndpoint}</div>
                  <div className="settings-code-block__line">POST {merchantCredentialEndpoint}</div>
                  <div className="settings-code-block__line">DELETE {merchantCredentialEndpoint}</div>
                </div>
              </div>
              <div className="mb-3">
                <Form.Label>Token Exchange Endpoint</Form.Label>
                <div className="settings-code-block">
                  <div className="settings-code-block__line">POST {merchantTokenEndpoint}</div>
                </div>
              </div>
              {renderExpandableSection(
                'Example cURL: exchange short-lived access token',
                'Use this only from your backend. Do not put clientSecret in browser code.',
                <Form.Control
                  as="textarea"
                  rows={9}
                  value={`curl -X POST "${merchantTokenEndpoint}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "clientId": "YOUR_CLIENT_ID",
    "clientSecret": "YOUR_CLIENT_SECRET"
  }'

# Response includes accessToken, accessTokenExpiredAt, and expiresInSeconds.`}
                  readOnly
                  className="settings-code-field"
                  style={{ '--code-color': '#7dd3a4' } as React.CSSProperties}
                />
              )}
            </Card.Body>
          </Card>

          <Card className="operator-card mb-4">
            <Card.Header className="operator-card__header">
              <h5 className="operator-card__title">Merchant Quick Start</h5>
              <small className="operator-card__subtitle">Keep this as your practical integration checklist when wiring a new merchant backend.</small>
            </Card.Header>
            <Card.Body>
              <div className="settings-section-note mb-3">
                Fast path for a new backend integration:
              </div>
              <ol className="mb-4 ps-3">
                <li>Generate merchant server credentials in the dashboard and store them in your backend.</li>
                <li>Exchange those credentials for a short-lived access token.</li>
                <li>Let RizzPay select the best available card or bank route for the payer.</li>
                <li>Create the deposit session and store <code>paymentId</code>, <code>checkoutUrl</code>, and <code>walletPayment.walletAddress</code>.</li>
                <li>Set an optional trusted <code>returnUrl</code> so the final card can show <code>Go back to merchant</code>.</li>
                <li>Redirect or embed the returned <code>checkoutUrl</code> for the payer.</li>
                <li>Verify signed webhooks and use deposit polling for recovery or reconciliation.</li>
              </ol>

              <div className="settings-api-access__details-stack">
                {renderExpandableSection(
                  'Example cURL: create deposit',
                  'Use bearer auth, store paymentId plus checkoutUrl, and include returnUrl when you want redirect-back UX.',
                  <Form.Control
                    as="textarea"
                    rows={14}
                    value={`curl -X POST "${createDepositEndpoint}" \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer YOUR_ACCESS_TOKEN" \\
  -d '{
    "userId": ${userId || 0},
    "fiatCurrency": "USD",
    "amount": 100,
    "returnUrl": "https://merchant.example.com/deposits/complete?merchantTransactionId=deposit-20260330-001",
    "merchantReferences": {
      "merchantTransactionId": "deposit-20260330-001",
      "merchantUserId": "player-4421",
      "merchantSessionId": "session-abc-77",
      "idempotencyKey": "deposit-20260330-001-attempt-1"
    }
  }'`}
                    readOnly
                    className="settings-code-field"
                    style={{ '--code-color': '#7dd3a4' } as React.CSSProperties}
                  />,
                  true
                )}

                {renderExpandableSection(
                  'Example cURL: poll deposit snapshot',
                  'Use paymentId-first polling for recovery, reconciliation, or support tools.',
                  <Form.Control
                    as="textarea"
                    rows={6}
                    value={`curl -X GET "${depositSnapshotEndpoint}" \\
  -H "Authorization: Bearer YOUR_ACCESS_TOKEN"

curl -X GET "${depositEventsEndpoint}" \\
  -H "Authorization: Bearer YOUR_ACCESS_TOKEN"`}
                    readOnly
                    className="settings-code-field"
                    style={{ '--code-color': '#7dd3a4' } as React.CSSProperties}
                  />
                )}
              </div>
            </Card.Body>
          </Card>

          <Card className="operator-card mb-4">
            <Card.Header className="operator-card__header">
              <h5 className="operator-card__title">Integration Resources</h5>
              <small className="operator-card__subtitle">Keep the quick-start links visible. Tuck the long setup references behind expandable sections.</small>
            </Card.Header>
            <Card.Body>
              <div className="mb-4">
                <Form.Label>Start Here</Form.Label>
                {renderResourceLinks(startHereResources)}
              </div>

              <div className="mb-4">
                <Form.Label>API Tools</Form.Label>
                {renderResourceLinks(apiToolResources)}
              </div>

              <div className="mb-4">
                <Form.Label>Webhooks, CRM, And Go-Live</Form.Label>
                {renderResourceLinks(webhookResources)}
              </div>

              <div className="mb-4">
                <Form.Label>Webhook Receiver Starters</Form.Label>
                {renderResourceLinks(starterResources)}
              </div>

              <div className="settings-api-access__details-stack">
                {renderExpandableSection(
                  'Postman environment values to fill',
                  'Use this when setting up the collection for the first time.',
                  <Form.Control
                    as="textarea"
                    rows={11}
                    value={`baseUrl=${normalizedApiBaseUrl}
clientId=YOUR_CLIENT_ID
clientSecret=YOUR_CLIENT_SECRET
accessToken=YOUR_SHORT_LIVED_ACCESS_TOKEN
legacyApiKey=YOUR_OPTIONAL_LEGACY_API_KEY
bearerToken=YOUR_DASHBOARD_BEARER_TOKEN
userId=${userId || 0}
fiatCurrency=USD
amount=100
merchantTransactionId=deposit-20260330-001
merchantUserId=player-4421
merchantSessionId=session-abc-77
idempotencyKey=deposit-20260330-001-attempt-1
webhookUrl=https://merchant.example.com/platform-webhooks`}
                    readOnly
                    className="settings-code-field"
                    style={{ '--code-color': '#7dd3a4' } as React.CSSProperties}
                  />
                )}

                {renderExpandableSection(
                  'Webhook receiver starter .env',
                  'Fastest way to prepare the sample Node or .NET receiver locally.',
                  <>
                    <Form.Control
                      as="textarea"
                      rows={5}
                      value={`PORT=5085
WEBHOOK_ROUTE=/platform-webhooks
WEBHOOK_TOKEN=YOUR_GENERATED_WEBHOOK_TOKEN
EVENT_LOG_PATH=./received-events.jsonl`}
                      readOnly
                      className="settings-code-field"
                      style={{ '--code-color': '#7dd3a4' } as React.CSSProperties}
                    />
                    <div className="operator-muted-text mt-2 mb-0">
                      Use the Node sample for the quickest first test, or the .NET sample if your cashier or CRM backend already runs on ASP.NET.
                    </div>
                  </>
                )}

                {renderExpandableSection(
                  'Webhook troubleshooting first checks',
                  'Short operator checklist for failed or missing callback reports.',
                  <Form.Control
                    as="textarea"
                    rows={7}
                    value={`1. Confirm the registered webhook URL is correct.
2. Verify X-COR-Signature with the exact raw request body.
3. Make sure the webhook token matches the current generated token.
4. Return HTTP 200 quickly after persisting the event.
5. Deduplicate on payload.eventInfo.eventId.
6. Use deposit polling only for recovery or reconciliation.`}
                    readOnly
                    className="settings-code-field"
                    style={{ '--code-color': '#7dd3a4' } as React.CSSProperties}
                  />
                )}

                {renderExpandableSection(
                  'Go-live readiness',
                  'Final rollout checklist before a merchant sends live traffic.',
                  <Form.Control
                    as="textarea"
                    rows={7}
                    value={`1. Confirm production clientId, clientSecret, userId, webhook URL, and webhook token.
2. Confirm your backend exchanges credentials for short-lived access tokens.
3. Confirm signature verification uses the exact raw request body.
4. Confirm deposit creation and webhook processing are idempotent.
5. Agree the live credit rule: PayoutCompleted or TransactionCompleted.
6. Test webhook failure recovery and polling fallback before live traffic.`}
                    readOnly
                    className="settings-code-field"
                    style={{ '--code-color': '#7dd3a4' } as React.CSSProperties}
                  />
                )}
              </div>

              <div className="operator-muted-text mt-3 mb-0">
                A merchant team should be able to import the collection and environment together, set these variables once, create a hosted deposit, and verify signed callbacks without editing every request manually.
              </div>
            </Card.Body>
          </Card>

          {/* Webhook Section */}
          <Card className="operator-card mb-4">
            <Card.Header className="operator-card__header">
              <h5 className="operator-card__title">Webhook</h5>
              <small className="operator-card__subtitle">Generate the signing secret first, then point the endpoint at your backend receiver.</small>
            </Card.Header>
            <Card.Body>
              {webhookLoading ? (
                <div className="d-flex justify-content-center">
                  <Spinner animation="border" role="status" className="app-spinner">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                </div>
              ) : (
                <>
                  <div className="mb-3">
                    <Form.Label>Current Webhook URL</Form.Label>
                    {webhookUrl ? (
                      <div className="d-flex align-items-center">
                        <Form.Control
                          type="text"
                          value={webhookUrl}
                          readOnly
                          className="me-2 settings-code-field"
                        />
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={handleDeleteWebhook}
                        >
                          Delete
                        </Button>
                      </div>
                    ) : (
                      <p className="operator-muted-text mb-0">No webhook URL configured yet.</p>
                    )}
                    <Form.Text>
                      Set or replace your webhook endpoint after you create a signing secret.
                    </Form.Text>
                  </div>

                  <div className="mb-3">
                    <Form.Label>Webhook Secret</Form.Label>
                    {webhookToken ? (
                      <div className="d-flex align-items-center mb-2">
                        <Form.Control
                          type="text"
                          value={webhookToken}
                          readOnly
                          className="me-2 settings-code-field"
                          style={{ '--code-color': '#7dd3a4' } as React.CSSProperties}
                        />
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => copyToClipboard(webhookToken, 'Webhook secret copied to clipboard')}
                        >
                          Copy
                        </Button>
                      </div>
                    ) : webhookTokenName ? (
                      <Form.Control
                        type="text"
                        value={webhookTokenName}
                        readOnly
                        className="settings-code-field mb-2"
                      />
                    ) : (
                      <p className="operator-muted-text mb-2">
                        No webhook secret generated yet. Create one before setting your webhook URL.
                      </p>
                    )}

                    <div className="settings-code-block mb-3">
                      <div className="settings-code-block__line">
                        Stored secret: {webhookTokenName || 'Not available yet'}
                      </div>
                      <div className="settings-code-block__line">
                        Last rotated: {formatTimestampLabel(webhookTokenRotatedAt)}
                      </div>
                      <div className="settings-code-block__line">
                        Endpoint last validated: {formatTimestampLabel(webhookEndpointValidatedAt)}
                      </div>
                    </div>

                    <div className="settings-callout settings-callout--warning mb-3">
                      Rotating the webhook secret invalidates the old signing secret immediately. Update your receiver before you save the next webhook endpoint change.
                    </div>

                    <Button
                      variant="outline-primary"
                      onClick={handleGenerateWebhookToken}
                      disabled={webhookTokenLoading}
                    >
                      {webhookTokenLoading ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2 app-spinner" />
                          Generating...
                        </>
                      ) : webhookTokenName ? (
                        'Rotate Webhook Secret'
                      ) : (
                        'Generate Webhook Secret'
                      )}
                    </Button>
                  </div>

                  <div className="mb-3">
                    <Form.Label>{webhookUrl ? 'Replace Webhook URL' : 'Set Webhook URL'}</Form.Label>
                    {!webhookTokenName && !webhookToken && (
                      <div className="settings-callout settings-callout--warning mb-3">
                        Generate a webhook secret first before setting a webhook URL.
                      </div>
                    )}
                    <div className="d-flex align-items-center">
                      <Form.Control
                        type="text"
                        value={newWebhookUrl}
                        onChange={(e) => setNewWebhookUrl(e.target.value)}
                        placeholder={webhookUrl || 'https://your-domain.com/webhook'}
                        disabled={!webhookTokenName && !webhookToken}
                        className="me-2 settings-code-field"
                        style={!webhookTokenName && !webhookToken ? { cursor: 'not-allowed' } : undefined}
                      />
                      <Button
                        variant="primary"
                        onClick={handleSetWebhook}
                        disabled={webhookLoading || (!webhookTokenName && !webhookToken)}
                      >
                        {webhookUrl ? 'Replace' : 'Set'}
                      </Button>
                    </div>
                  </div>

                  <div className="settings-api-access__details-stack mt-4">
                    {renderExpandableSection(
                      'Webhook documentation',
                      'Headers, configuration payload, and transaction payload shape for your receiver.',
                      <>
                        <div className="mb-3">
                          <h6 className="operator-card__title mb-2">Webhook Headers</h6>
                          <p className="operator-muted-text mb-2">
                            Signature is the base64 HMAC SHA256 of the raw request body.
                          </p>
                          <p className="operator-muted-text mb-3">
                            Signature secret is the current webhook secret shown in the dashboard right after generation or rotation.
                          </p>
                          <div className="settings-code-block">
                            <div className="settings-code-block__line">X-COR-Signature: xxxxxx</div>
                          </div>
                        </div>

                        <div className="mb-3">
                          <h6 className="operator-card__title mb-2">Webhook Payload</h6>
                          <p className="operator-muted-text mb-2">
                            <strong>On Endpoint Configuration:</strong>
                          </p>
                          <Form.Control
                            as="textarea"
                            rows={4}
                            value={JSON.stringify({
                              action: "Authorization",
                              payload: "YOUR_CURRENT_WEBHOOK_SECRET"
                            }, null, 2)}
                            readOnly
                            className="settings-code-field mb-3"
                            style={{ '--code-color': '#7dd3a4' } as React.CSSProperties}
                          />

                          <p className="operator-muted-text mb-2">
                            <strong>On Transaction:</strong>
                          </p>
                          <Form.Control
                            as="textarea"
                            rows={20}
                            value={JSON.stringify({
                              action: "TransactionCompleted",
                              payload: {
                                transaction: {
                                  transactionId: 123,
                                  publicPaymentId: "abc123",
                                  lifecycleStatus: "Settled",
                                  displayStatus: "Underpaid (Settled)",
                                  paymentSource: "DirectCrypto",
                                  provider: "CryptoProcessing",
                                  fiatCurrency: "USD",
                                  fiatAmount: 100,
                                  cryptoCurrency: "USDC",
                                  receivedCryptoAmount: 1,
                                  distributedCryptoAmount: 1,
                                  settlementTxHash: "0x...",
                                  merchantReferences: {
                                    merchantTransactionId: "deposit-20260330-001",
                                    merchantUserId: "player-4421",
                                    merchantSessionId: "session-abc-77",
                                    idempotencyKey: "deposit-20260330-001-attempt-1"
                                  }
                                },
                                eventInfo: {
                                  eventId: 434,
                                  eventSequence: 6,
                                  eventType: "TransactionCompleted",
                                  eventCategory: "settlement",
                                  finalized: true,
                                  occurredAt: "2026-03-30T13:57:42.122Z"
                                },
                                checkoutSessions: [],
                                settlements: [
                                  {
                                    role: "Processor",
                                    amount: "0.1",
                                    settlementTxHash: "0x..."
                                  },
                                  {
                                    role: "Merchant",
                                    amount: "0.895",
                                    settlementTxHash: "0x..."
                                  }
                                ]
                              }
                            }, null, 2)}
                            readOnly
                            className="settings-code-field"
                            style={{ '--code-color': '#7dd3a4' } as React.CSSProperties}
                          />
                          <p className="operator-muted-text mt-3 mb-0">
                            Use <code>payload.transaction.publicPaymentId</code> as the RizzPay payment identifier. Deduplicate on <code>payload.eventInfo.eventId</code> and treat <code>payload.eventInfo.finalized = true</code> as the clean terminal signal for strict accounting flows.
                          </p>

                          <p className="operator-muted-text mt-3 mb-2">
                            <strong>On Direct USDC Underpayment:</strong>
                          </p>
                          <Form.Control
                            as="textarea"
                            rows={30}
                            value={JSON.stringify({
                              action: "PartialPaymentReceived",
                              payload: {
                                transaction: {
                                  transactionId: 123,
                                  publicPaymentId: "abc123",
                                  lifecycleStatus: "Pending",
                                  displayStatus: "Underpayment Received (Awaiting Completion)",
                                  paymentSource: "DirectCrypto",
                                  provider: "CryptoProcessing",
                                  fiatCurrency: "USD",
                                  fiatAmount: 100,
                                  cryptoCurrency: "USDC",
                                  cryptoAmount: 99.4,
                                  receivedCryptoAmount: 99.4,
                                  distributedCryptoAmount: null,
                                  quotedCryptoAmount: 100,
                                  settlementTxHash: null,
                                  disposableAddress: "0x...",
                                  merchantReferences: {
                                    merchantTransactionId: "deposit-20260330-001",
                                    merchantUserId: "player-4421",
                                    merchantSessionId: "session-abc-77",
                                    idempotencyKey: "deposit-20260330-001-attempt-1"
                                  }
                                },
                                eventInfo: {
                                  eventId: 435,
                                  eventSequence: 4,
                                  eventType: "PartialPaymentReceived",
                                  eventCategory: "payment",
                                  finalized: false,
                                  source: "direct-payment-worker",
                                  payload: {
                                    quotedCryptoAmount: 100,
                                    actualCryptoAmount: 99.4,
                                    remainingCryptoAmount: 0.6,
                                    completionToleranceRatio: 0.005,
                                    topUpSupported: true
                                  },
                                  occurredAt: "2026-03-30T13:57:42.122Z"
                                },
                                checkoutSessions: [],
                                settlements: []
                              }
                            }, null, 2)}
                            readOnly
                            className="settings-code-field"
                            style={{ '--code-color': '#7dd3a4' } as React.CSSProperties}
                          />
                          <p className="operator-muted-text mt-3 mb-0">
                            Do not credit while <code>displayStatus = Underpayment Received (Awaiting Completion)</code>. The payer can complete the missing direct USDC amount in the RizzPay checkout, and a later settled callback will arrive after completion.
                          </p>

                          <p className="operator-muted-text mt-3 mb-2">
                            <strong>On Provider Timeout:</strong>
                          </p>
                          <Form.Control
                            as="textarea"
                            rows={28}
                            value={JSON.stringify({
                              action: "ProviderStatusUpdated",
                              payload: {
                                transaction: {
                                  transactionId: 123,
                                  publicPaymentId: "abc123",
                                  lifecycleStatus: "Expired",
                                  displayStatus: "Expired (Provider Timeout)",
                                  paymentSource: "FiatOnramp",
                                  provider: "Ramp",
                                  fiatCurrency: "USD",
                                  fiatAmount: 1453,
                                  cryptoCurrency: "USDC",
                                  recipientWallet: "0x...",
                                  disposableAddress: "0x...",
                                  merchantReferences: {
                                    merchantTransactionId: "deposit-20260330-001",
                                    merchantUserId: "player-4421",
                                    merchantSessionId: "session-abc-77",
                                    idempotencyKey: "deposit-20260330-001-attempt-1"
                                  }
                                },
                                eventInfo: {
                                  eventId: 435,
                                  eventSequence: 3,
                                  eventType: "ProviderStatusUpdated",
                                  eventCategory: "provider",
                                  finalized: false,
                                  source: "onramper-timeout",
                                  checkoutSessionId: "checkout-session-id",
                                  payload: {
                                    providerTransactionId: "provider-side-id",
                                    providerStatus: "ProviderTimeout",
                                    transactionStatus: "Expired",
                                    failure: {
                                      message: "Provider timed out before sending a final payment status."
                                    },
                                    staleAfterMinutes: 60,
                                    checkoutSessionCreatedAt: "2026-03-30T12:57:42.122Z",
                                    timedOutAt: "2026-03-30T13:57:42.122Z"
                                  },
                                  occurredAt: "2026-03-30T13:57:42.122Z"
                                }
                              }
                            }, null, 2)}
                            readOnly
                            className="settings-code-field"
                            style={{ '--code-color': '#7dd3a4' } as React.CSSProperties}
                          />
                          <p className="operator-muted-text mt-3 mb-0">
                            For provider timeout handling, match <code>action = ProviderStatusUpdated</code> and <code>payload.eventInfo.payload.providerStatus = ProviderTimeout</code>, then mark the payment expired or failed on your side.
                          </p>
                        </div>
                      </>,
                      true
                    )}
                  </div>
                </>
              )}
            </Card.Body>
          </Card>
      </div>
    );
  };

  const renderOnramperFlowTab = () => {
    if (userRole === 'Admin') {
      const queueFilterOptions: FiatOnrampApprovalQueueFilter[] = ['Pending', 'Approved', 'Rejected', 'Disabled', 'NotRequested', 'All'];

      return (
        <div className="settings-onramper-flow">
          <Card className="operator-card mb-4 settings-api-access__overview">
            <Card.Body>
              <div className="settings-api-access__eyebrow">Risk-controlled enablement</div>
              <div className="settings-api-access__hero">
                <div className="settings-api-access__hero-copy">
                  <h5 className="operator-card__title mb-2">Onramper flow approvals</h5>
                  <p className="operator-muted-text mb-0">
                    Approve or disable fiat onramp availability per merchant. Direct crypto remains available regardless of this switch.
                  </p>
                </div>
                <div className="settings-api-access__pill-row">
                  <span className="settings-api-access__pill">Manual review</span>
                  <span className="settings-api-access__pill">Telegram contact required</span>
                  <span className="settings-api-access__pill">API-level enforcement</span>
                </div>
              </div>
              <div className="settings-api-access__quick-actions">
                <Form.Select
                  value={fiatOnrampQueueFilter}
                  onChange={(event) => handleFiatOnrampQueueFilterChange(event.target.value as FiatOnrampApprovalQueueFilter)}
                  style={{ maxWidth: '220px' }}
                >
                  {queueFilterOptions.map((filterKey) => (
                    <option key={filterKey} value={filterKey}>
                      {filterKey === 'All' ? 'All requests' : `${filterKey} requests`}
                    </option>
                  ))}
                </Form.Select>
                <Button
                  variant="outline-primary"
                  onClick={() => void fetchFiatOnrampQueue(fiatOnrampQueueFilter)}
                  disabled={fiatOnrampQueueLoading}
                >
                  {fiatOnrampQueueLoading ? 'Refreshing...' : 'Refresh queue'}
                </Button>
              </div>
            </Card.Body>
          </Card>

          <Card className="operator-card">
            <Card.Header className="operator-card__header">
              <h5 className="operator-card__title mb-0">Merchant requests</h5>
            </Card.Header>
            <Card.Body>
              {fiatOnrampQueueLoading ? (
                <div className="d-flex justify-content-center py-4">
                  <Spinner animation="border" role="status" className="app-spinner">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                </div>
              ) : fiatOnrampQueue.length === 0 ? (
                <p className="settings-empty mb-0">No merchant requests in this filter.</p>
              ) : (
                <Table responsive className="app-data-table settings-table">
                  <thead>
                    <tr>
                      <th>Merchant</th>
                      <th>Status</th>
                      <th>Telegram</th>
                      <th>Requested</th>
                      <th>Reviewed</th>
                      <th>Review note</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fiatOnrampQueue.map((item) => {
                      const isSaving = Boolean(fiatOnrampActionByMerchant[item.merchantUserId]);
                      const reviewNote = fiatOnrampReviewNotes[item.merchantUserId] ?? item.fiatOnrampApprovalReviewNote ?? '';

                      return (
                        <tr key={item.merchantUserId}>
                          <td>
                            <div className="fw-semibold">{item.merchantName || `Merchant #${item.merchantUserId}`}</div>
                            <div className="operator-muted-text">{item.merchantEmail || 'No email available'}</div>
                            <div className="operator-muted-text">{item.merchantRole}</div>
                          </td>
                          <td>{getFiatOnrampStatusLabel(item.fiatOnrampApprovalStatus, item.fiatOnrampEnabled)}</td>
                          <td>{item.fiatOnrampApprovalTelegramUsername || 'Not provided'}</td>
                          <td>{formatTimestampLabel(item.fiatOnrampApprovalRequestedAtUtc)}</td>
                          <td>{formatTimestampLabel(item.fiatOnrampApprovalReviewedAtUtc)}</td>
                          <td style={{ minWidth: '220px' }}>
                            <Form.Control
                              type="text"
                              value={reviewNote}
                              onChange={(event) => setFiatOnrampReviewNotes((prev) => ({
                                ...prev,
                                [item.merchantUserId]: event.target.value,
                              }))}
                              placeholder="Optional note shown to merchant"
                              className="settings-code-field"
                            />
                          </td>
                          <td>
                            <div className="d-flex flex-wrap gap-2">
                              <Button
                                variant="outline-success"
                                size="sm"
                                disabled={isSaving || item.fiatOnrampEnabled}
                                onClick={() => void handleSetFiatOnrampMerchantState(item, true)}
                              >
                                {isSaving ? 'Saving...' : 'Enable'}
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                disabled={isSaving || (!item.fiatOnrampEnabled && item.fiatOnrampApprovalStatus !== 'Pending')}
                                onClick={() => void handleSetFiatOnrampMerchantState(item, false)}
                              >
                                {isSaving ? 'Saving...' : 'Disable'}
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </div>
      );
    }

    const currentStatus = fiatOnrampStatus?.fiatOnrampApprovalStatus ?? 'NotRequested';
    const fiatOnrampEnabled = Boolean(fiatOnrampStatus?.fiatOnrampEnabled);
    const statusLabel = getFiatOnrampStatusLabel(currentStatus, fiatOnrampEnabled);
    const statusTone = getFiatOnrampStatusTone(currentStatus, fiatOnrampEnabled);
    const hasPendingRequest = currentStatus === 'Pending';
    const requestLocked = hasPendingRequest || fiatOnrampEnabled;
    const telegramValue = fiatOnrampRequestTelegram;

    return (
      <div className="settings-onramper-flow">
        <Card className="operator-card mb-4 settings-api-access__overview">
          <Card.Body>
            <div className="settings-api-access__eyebrow">Optional merchant add-on</div>
            <div className="settings-api-access__hero">
              <div className="settings-api-access__hero-copy">
                <h5 className="operator-card__title mb-2">Fiat onramp approval request</h5>
                <p className="operator-muted-text mb-0">
                  Direct crypto checkout stays enabled by default. Request fiat onramp access here if you need card and bank provider options.
                </p>
              </div>
              <div className="settings-api-access__pill-row">
                <span className="settings-api-access__pill">Direct crypto default</span>
                <span className="settings-api-access__pill">Admin approval required</span>
                <span className="settings-api-access__pill">Telegram follow-up</span>
              </div>
            </div>
            <div className="settings-api-access__status-grid">
              <div className={`settings-api-access__status-card settings-api-access__status-card--${statusTone}`}>
                <div className="settings-api-access__status-label">Fiat onramp status</div>
                <div className="settings-api-access__status-value">{statusLabel}</div>
                <div className="settings-api-access__status-copy">
                  {fiatOnrampEnabled
                    ? 'Fiat providers can now be offered to your payers.'
                    : 'Only direct crypto flow is currently available.'}
                </div>
              </div>
              <div className="settings-api-access__status-card">
                <div className="settings-api-access__status-label">Telegram contact</div>
                <div className="settings-api-access__status-value">
                  {fiatOnrampStatus?.fiatOnrampApprovalTelegramUsername || 'Not submitted yet'}
                </div>
                <div className="settings-api-access__status-copy">
                  Admin contacts this username for merchant due diligence.
                </div>
              </div>
              <div className="settings-api-access__status-card">
                <div className="settings-api-access__status-label">Last review</div>
                <div className="settings-api-access__status-value">
                  {formatTimestampLabel(fiatOnrampStatus?.fiatOnrampApprovalReviewedAtUtc)}
                </div>
                <div className="settings-api-access__status-copy">
                  {fiatOnrampStatus?.fiatOnrampApprovalReviewNote || 'No review note yet.'}
                </div>
              </div>
            </div>
            <div className="settings-api-access__quick-actions">
              <Button
                variant="outline-primary"
                onClick={() => void fetchFiatOnrampStatus()}
                disabled={fiatOnrampStatusLoading}
              >
                {fiatOnrampStatusLoading ? 'Refreshing...' : 'Refresh status'}
              </Button>
            </div>
          </Card.Body>
        </Card>

        <Card className="operator-card">
          <Card.Header className="operator-card__header">
            <h5 className="operator-card__title mb-0">Request access</h5>
          </Card.Header>
          <Card.Body>
            <div className="settings-callout settings-callout--warning mb-3">
              Use your real Telegram username so the admin team can contact you for verification before enabling fiat onramp providers.
            </div>
            <Form.Group controlId="fiatOnrampTelegram" className="mb-3">
              <Form.Label>Telegram username</Form.Label>
              <Form.Control
                type="text"
                value={telegramValue}
                onChange={(event) => setFiatOnrampRequestTelegram(event.target.value)}
                placeholder="@your_handle"
                className="settings-code-field"
                disabled={requestLocked}
              />
            </Form.Group>
            {fiatOnrampStatus?.fiatOnrampApprovalRequestedAtUtc && (
              <p className="operator-muted-text mb-3">
                Last request submitted: {formatTimestampLabel(fiatOnrampStatus.fiatOnrampApprovalRequestedAtUtc)}
              </p>
            )}
            <div className="d-flex flex-wrap gap-2">
              <Button
                variant="primary"
                onClick={handleSubmitFiatOnrampRequest}
                disabled={requestLocked || fiatOnrampRequestSubmitting}
              >
                {fiatOnrampRequestSubmitting
                  ? 'Submitting...'
                  : requestLocked
                    ? (fiatOnrampEnabled ? 'Already enabled' : 'Request pending')
                    : 'Request fiat onramp access'}
              </Button>
              {fiatOnrampStatus?.fiatOnrampApprovalStatus === 'Rejected' && (
                <Button
                  variant="outline-primary"
                  onClick={handleSubmitFiatOnrampRequest}
                  disabled={fiatOnrampRequestSubmitting}
                >
                  Resubmit request
                </Button>
              )}
            </div>
          </Card.Body>
        </Card>
      </div>
    );
  };

  const renderIframeTab = () => {
    const maskedEmbedToken = embedTokenName?.trim() ?? '';
    const effectiveEmbedToken = generatedEmbedToken.trim();
    const hasExistingEmbedToken = maskedEmbedToken.length > 0;
    const hasUsableEmbedToken = effectiveEmbedToken.length > 0;
    const hasAnyEmbedToken = hasExistingEmbedToken || hasUsableEmbedToken;
    const themeParam = encodeURIComponent(iframeTheme);
    const normalizedShopAmount = shopIframeAmount.trim();
    const hasValidShopAmount = normalizedShopAmount.length > 0 && Number.isFinite(Number(normalizedShopAmount)) && Number(normalizedShopAmount) > 0;
    const flexibleIframeUrl = hasUsableEmbedToken
      ? `${window.location.origin}/iframe/${effectiveEmbedToken}?theme=${themeParam}`
      : '';
    const shopIframeUrl = hasUsableEmbedToken && hasValidShopAmount
      ? `${window.location.origin}/iframe/${effectiveEmbedToken}?theme=${themeParam}&currency=${encodeURIComponent(shopIframeCurrency)}&amount=${encodeURIComponent(normalizedShopAmount)}&lockAmount=1`
      : '';
    const activeIframeUrl = iframeLaunchMode === 'shop' ? shopIframeUrl : flexibleIframeUrl;
    const flexibleIframeSnippet = hasUsableEmbedToken
      ? `<iframe src="${flexibleIframeUrl}" style="width:100%;min-height:720px;border:0;" title="RizzPay Hosted Checkout"></iframe>`
      : '';
    const shopIframeSnippet = hasUsableEmbedToken && hasValidShopAmount
      ? `<iframe src="${shopIframeUrl}" style="width:100%;min-height:720px;border:0;" title="RizzPay Hosted Checkout"></iframe>`
      : '';
    const activeIframeSnippet = iframeLaunchMode === 'shop' ? shopIframeSnippet : flexibleIframeSnippet;
    const iframeReadinessCards = [
      {
        title: 'Embed token',
        value: hasAnyEmbedToken ? 'Ready' : 'Needs setup',
        copy: hasAnyEmbedToken
          ? 'A browser-safe token exists for this iframe workspace.'
          : 'Generate an embed token before you copy the iframe URL or snippet.',
        tone: hasAnyEmbedToken ? 'ready' : 'action'
      },
      {
        title: 'Preview link',
        value: hasUsableEmbedToken ? 'Available now' : 'Pending',
        copy: hasUsableEmbedToken
          ? 'You can open a live preview or copy the launch URL immediately.'
          : 'The preview URL appears only when a fresh full token is visible.',
        tone: hasUsableEmbedToken ? 'ready' : 'neutral'
      },
      {
        title: 'Launch mode',
        value: iframeLaunchMode === 'shop' ? 'Fixed amount' : 'Amount entry',
        copy: iframeLaunchMode === 'shop'
          ? 'The payer opens with a preset amount that stays locked.'
          : 'The payer enters the amount inside the iframe before paying.',
        tone: iframeLaunchMode === 'shop' ? 'action' : 'neutral'
      },
      {
        title: 'Theme',
        value: IFRAME_THEME_PRESETS[iframeTheme].label,
        copy: IFRAME_THEME_PRESETS[iframeTheme].description,
        tone: 'ready'
      }
    ] as const;
    const iframeSetupSteps = [
      'Generate or rotate the browser-safe embed token.',
      'Choose amount-entry or fixed-amount mode.',
      'Copy the iframe URL or embed snippet while the full token is visible.'
    ] as const;

      return (
        <div className="settings-iframe">
          <Card className="operator-card settings-iframe__overview">
            <Card.Body>
              <div className="settings-iframe__eyebrow">Browser-safe embed setup</div>
              <div className="settings-iframe__hero">
                <div>
                  <h5 className="operator-card__title mb-2">Launch one-page checkout inside your own page</h5>
                  <p className="operator-muted-text mb-0">
                    Use amount-entry mode when the payer should enter an amount, or fixed-amount mode when the cart total is already known.
                  </p>
                </div>
                <div className="settings-iframe__pill-row">
                  <span className="settings-iframe__pill">Embed token</span>
                  <span className="settings-iframe__pill">Amount entry or fixed</span>
                  <span className="settings-iframe__pill">RizzPay dark checkout</span>
                  <span className="settings-iframe__pill">Parent-page events</span>
                </div>
              </div>
              <div className="settings-iframe__status-grid">
                {iframeReadinessCards.map(card => (
                  <div
                    key={card.title}
                    className={`settings-iframe__status-card settings-iframe__status-card--${card.tone}`}
                  >
                    <div className="settings-iframe__status-label">{card.title}</div>
                    <div className="settings-iframe__status-value">{card.value}</div>
                    <div className="settings-iframe__status-copy">{card.copy}</div>
                  </div>
                ))}
              </div>
              <div className="settings-iframe__quick-actions">
                <Button
                  variant="primary"
                  onClick={handleGenerateEmbedToken}
                  disabled={embedTokenLoading}
                >
                  {hasExistingEmbedToken ? 'Rotate embed token' : 'Generate embed token'}
                </Button>
                <Button
                  variant="outline-primary"
                  onClick={() => copyToClipboard(activeIframeUrl, 'Iframe URL copied to clipboard')}
                  disabled={!activeIframeUrl}
                >
                  Copy iframe URL
                </Button>
                <Button
                  variant="outline-info"
                  onClick={() => window.open(activeIframeUrl, '_blank', 'noopener,noreferrer')}
                  disabled={!activeIframeUrl}
                >
                  Open live preview
                </Button>
              </div>
            </Card.Body>
          </Card>

          <Row className="g-4">
            <Col xl={7}>
              <Card className="operator-card">
                <Card.Header className="operator-card__header">
                  <h5 className="operator-card__title">Embed Token And Launch Assets</h5>
                  <small className="operator-card__subtitle">Generate the browser-safe token, choose the launch mode, then copy the final URL or snippet.</small>
                </Card.Header>
                <Card.Body>
                  <div className="settings-guide-grid mb-4">
                    {iframeSetupSteps.map((step, index) => (
                      <div key={step} className="settings-guide-card settings-guide-card--compact">
                        <div className="settings-guide-card__step">Step {index + 1}</div>
                        <div className="settings-guide-card__copy">{step}</div>
                      </div>
                    ))}
                  </div>

                  {hasExistingEmbedToken && !hasUsableEmbedToken && (
                    <div className="settings-callout settings-callout--warning mb-4">
                      A token already exists, but the full value is hidden. Rotate it when you need a fresh URL or snippet.
                    </div>
                  )}

                  <Form.Group className="mb-4">
                    <Form.Label>Launch Mode</Form.Label>
                    <div className="settings-iframe__mode-toggle">
                      <button
                        type="button"
                        className={`settings-iframe__mode-option ${iframeLaunchMode === 'flexible' ? 'settings-iframe__mode-option--active' : ''}`}
                        onClick={() => setIframeLaunchMode('flexible')}
                      >
                        <span className="settings-iframe__mode-title">Amount-entry checkout</span>
                        <span className="settings-iframe__mode-copy">Payer enters the amount inside the iframe.</span>
                      </button>
                      <button
                        type="button"
                        className={`settings-iframe__mode-option ${iframeLaunchMode === 'shop' ? 'settings-iframe__mode-option--active' : ''}`}
                        onClick={() => setIframeLaunchMode('shop')}
                      >
                        <span className="settings-iframe__mode-title">Fixed-amount checkout</span>
                        <span className="settings-iframe__mode-copy">Cart amount is preset and stays locked.</span>
                      </button>
                    </div>
                  </Form.Group>

                  <Form.Group className="mb-4">
                    <Form.Label>Embed Token</Form.Label>
                    <Form.Control
                      type="text"
                      value={hasUsableEmbedToken ? effectiveEmbedToken : maskedEmbedToken}
                      placeholder="Generate an embed token to create the iframe URL"
                      readOnly
                      className="settings-code-field"
                      style={hasUsableEmbedToken ? ({ '--code-color': '#7dd3a4' } as React.CSSProperties) : undefined}
                    />
                    <Form.Text>
                      This token only authorizes embedded provider discovery and deposit creation. It does not grant full merchant API access.
                    </Form.Text>
                    <div className="d-flex flex-wrap gap-2 mt-3">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={handleGenerateEmbedToken}
                        disabled={embedTokenLoading}
                      >
                        {embedTokenLoading ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2 app-spinner" />
                            Generating...
                          </>
                        ) : (
                          hasExistingEmbedToken ? 'Rotate embed token' : 'Generate embed token'
                        )}
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={handleDeleteEmbedToken}
                        disabled={embedTokenLoading || !hasAnyEmbedToken}
                      >
                        Delete embed token
                      </Button>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => copyToClipboard(effectiveEmbedToken, 'Embed token copied to clipboard')}
                        disabled={!hasUsableEmbedToken}
                      >
                        Copy token
                      </Button>
                    </div>
                  </Form.Group>

                  {iframeLaunchMode === 'shop' && (
                    <div className="settings-iframe__shop-config mb-4">
                      <div className="settings-iframe__shop-header">
                        <div className="settings-iframe__preview-label">Fixed amount setup</div>
                        <div className="settings-iframe__preview-copy">
                          Use this when the merchant already knows the cart total and wants the payer to land directly in the payment workspace.
                        </div>
                      </div>
                      <Row className="g-3">
                        <Col md={4}>
                          <Form.Group>
                            <Form.Label>Currency</Form.Label>
                            <Form.Select
                              value={shopIframeCurrency}
                              onChange={(e) => setShopIframeCurrency(e.target.value)}
                              className="operator-control"
                            >
                              {FIAT_CURRENCIES.map((currency) => (
                                <option key={currency} value={currency}>
                                  {currency}
                                </option>
                              ))}
                            </Form.Select>
                          </Form.Group>
                        </Col>
                        <Col md={8}>
                          <Form.Group>
                            <Form.Label>Preset amount</Form.Label>
                            <Form.Control
                              type="number"
                              min="0"
                              step="0.01"
                              value={shopIframeAmount}
                              onChange={(e) => setShopIframeAmount(e.target.value)}
                              className="operator-control"
                              placeholder="149.99"
                            />
                            <Form.Text>
                              This adds <code>amount</code>, <code>currency</code>, and <code>lockAmount=1</code> to the launch URL.
                            </Form.Text>
                          </Form.Group>
                        </Col>
                      </Row>
                    </div>
                  )}

                  <Form.Group className="mb-4">
                  <Form.Label>{iframeLaunchMode === 'shop' ? 'Fixed-amount iframe URL' : 'Amount-entry iframe URL'}</Form.Label>
                    <Form.Control
                      type="text"
                      value={activeIframeUrl}
                      placeholder="Generate an embed token to create the iframe URL"
                      readOnly
                      className="settings-code-field"
                      style={{ '--code-color': 'var(--app-blue)' } as React.CSSProperties}
                    />
                    <div className="d-flex flex-wrap gap-2 mt-3">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => copyToClipboard(activeIframeUrl, 'Iframe URL copied to clipboard')}
                        disabled={!activeIframeUrl}
                      >
                        Copy URL
                      </Button>
                      <Button
                        variant="outline-info"
                        size="sm"
                        onClick={() => window.open(activeIframeUrl, '_blank', 'noopener,noreferrer')}
                        disabled={!activeIframeUrl}
                      >
                        Open Preview
                      </Button>
                    </div>
                  </Form.Group>

                  <Form.Group>
                    <Form.Label>{iframeLaunchMode === 'shop' ? 'Fixed-amount embed snippet' : 'Amount-entry embed snippet'}</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      value={activeIframeSnippet}
                      placeholder="Generate an embed token to create the iframe snippet"
                      readOnly
                      className="settings-code-field"
                    />
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="mt-3"
                      onClick={() => copyToClipboard(activeIframeSnippet, 'Iframe snippet copied to clipboard')}
                      disabled={!activeIframeSnippet}
                    >
                      Copy Embed Code
                    </Button>
                  </Form.Group>
                </Card.Body>
              </Card>
            </Col>
            <Col xl={5}>
              <Card className="operator-card">
                <Card.Header className="operator-card__header">
                  <h5 className="operator-card__title">Design And Preview</h5>
                  <small className="operator-card__subtitle">Preview the embedded checkout using the same design as the RizzPay payment flow.</small>
                </Card.Header>
                <Card.Body>
                  <div className="settings-section-note mb-4">
                    The payer-facing iframe uses the same dark RizzPay checkout theme across amount entry and the hosted payment workspace. Server auth and hosted-deposit behavior stay the same.
                  </div>

                  <Form.Group className="mb-4">
                    <Form.Label>Checkout theme</Form.Label>
                    <div className="settings-iframe__theme-toggle">
                      {(['dark'] as IframeThemePreference[]).map((theme) => (
                        <button
                          key={theme}
                          type="button"
                          className={`settings-iframe__theme-option ${iframeTheme === theme ? 'settings-iframe__theme-option--active' : ''}`}
                          onClick={() => handleIframeThemeSelect(theme)}
                        >
                          <span className={`settings-iframe__theme-swatch settings-iframe__theme-swatch--${theme}`} />
                          <span>
                            <strong>{IFRAME_THEME_PRESETS[theme].label}</strong>
                            <small>{IFRAME_THEME_PRESETS[theme].description}</small>
                          </span>
                        </button>
                      ))}
                    </div>
                    <Form.Text>
                      Adds <code>theme={iframeTheme}</code> to the iframe URL. Older custom color launch options are ignored by the payer page so the payment experience stays visually consistent.
                    </Form.Text>
                  </Form.Group>

                  <div className="settings-iframe__preview-card">
                    <div className="settings-iframe__preview-label">Current launch mode</div>
                    <div className="settings-iframe__preview-value">
                      {hasUsableEmbedToken
                        ? iframeLaunchMode === 'shop'
                          ? 'Fixed-amount checkout ready'
                          : 'Amount-entry checkout ready'
                        : 'Waiting for fresh embed token'}
                    </div>
                    <div className="settings-iframe__preview-copy">
                      {hasUsableEmbedToken
                        ? iframeLaunchMode === 'shop'
                          ? 'Preview the fixed-amount flow to confirm the payer lands directly in the payment workspace.'
                          : 'Preview the amount-entry flow to confirm same-page payment behavior.'
                        : 'Generate or rotate the embed token when you need a new full URL for preview or embedding.'}
                    </div>
                  </div>
                </Card.Body>
              </Card>

              <Card className="operator-card settings-help-card mt-4">
                <Card.Header className="operator-card__header">
                  <h6 className="operator-card__title">Iframe Callback Events</h6>
                </Card.Header>
                <Card.Body>
                  <p className="operator-muted-text mb-3">
                    Use parent-page events only if your storefront needs analytics, loading states, or completion hooks.
                  </p>
                  <details className="settings-iframe__details">
                    <summary className="settings-iframe__details-summary">
                      Show callback example
                    </summary>
                    <Form.Control
                      as="textarea"
                      rows={14}
                      readOnly
                      value={`window.addEventListener('message', (event) => {
  if (event.data?.source !== 'COR_PAYMENT') return;

  switch (event.data.type) {
    case 'COR_IFRAME_READY':
    case 'COR_PAYMENT_PAGE_CREATED':
    case 'COR_PAYMENT_PAGE_LOADED':
    case 'COR_PAYMENT_STATUS_CHANGED':
    case 'COR_PAYMENT_FINALIZED':
    case 'COR_PROVIDER_SELECTED':
    case 'COR_PAYMENT_ERROR':
      console.log(event.data.type, event.data.payload);
      break;
  }
});`}
                      className="settings-code-field"
                    />
                  </details>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </div>
      );
  };

  const renderWidgetTab = () => {
    const widgetLogoUrl = `${process.env.PUBLIC_URL || ''}/rizzpay-mark.png`;
    const merchantDocsBasePath = `${process.env.PUBLIC_URL || ''}/merchant-integration`;
    const fallbackDownloadUrl = `${merchantDocsBasePath}/rizzpay-woocommerce-wc-release-013-plugincheck-clean-20260528-150226.zip`;
    const fallbackSetupGuideUrl = `${merchantDocsBasePath}/WOOCOMMERCE_WIDGET_SETUP_GUIDE_2026-05-21.md`;
    const fallbackChangelogUrl = `${merchantDocsBasePath}/WOOCOMMERCE_WIDGET_CHANGELOG_2026-05-21.md`;
    const hasWidgetMetadata = Boolean(widgetMetadata);
    const metadataDownloadUrl = widgetMetadata?.zip_url?.trim() || '';
    const downloadUrl = (() => {
      if (!metadataDownloadUrl) {
        return fallbackDownloadUrl;
      }

      try {
        const parsedUrl = new URL(metadataDownloadUrl, window.location.origin);
        const isRelativePath = metadataDownloadUrl.startsWith('/');
        const isSameOrigin = parsedUrl.origin === window.location.origin;

        return isRelativePath || isSameOrigin ? parsedUrl.toString() : fallbackDownloadUrl;
      } catch (urlError) {
        console.error('Invalid widget download URL in metadata, using fallback package.', urlError);
        return fallbackDownloadUrl;
      }
    })();
    const docsUrl = widgetMetadata?.docs_url?.trim() || fallbackSetupGuideUrl;
    const changelogUrl = widgetMetadata?.changelog_url?.trim() || fallbackChangelogUrl;
    const compatibilityNotes = widgetMetadata?.compatibility_notes?.trim() || 'Validated with WordPress 7.0 and WooCommerce 10.7.';
    const installSteps = [
      'In Settings > API Access, copy Client ID, Client Secret, and Webhook Secret first.',
      'Download the latest plugin zip from this tab.',
      'In WordPress go to Plugins > Add New > Upload Plugin, then install and activate.',
      'Open WooCommerce > Settings > Payments > RizzPay and save Client ID, Client Secret, Beneficiary User ID, Webhook Secret, and API Base URL.',
      'Return URL is automatic in Woo: the plugin sends the Woo order-received URL when the store URL is HTTPS (loopback HTTP allowed only for local dev).',
    ] as const;

    if (widgetLoading && !hasWidgetMetadata) {
      return (
        <Card className="operator-card">
          <Card.Body className="text-center">
            <Spinner animation="border" role="status" className="app-spinner">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </Card.Body>
        </Card>
      );
    }

    return (
      <div className="settings-widget">
        <Card className="operator-card settings-widget__overview">
          <Card.Body>
            <div className="settings-widget__eyebrow">WooCommerce distribution</div>
            <div className="settings-widget__hero">
              <div className="settings-widget__identity">
                <img src={widgetLogoUrl} alt="RizzPay" className="settings-widget__logo" />
                <div>
                  <h5 className="operator-card__title mb-2">{widgetMetadata?.name || 'RizzPay for WooCommerce'}</h5>
                  <p className="operator-muted-text mb-0">
                    Download the latest verified WooCommerce plugin package and onboarding docs from one place. This direct zip distribution works independently from WordPress.org listing status.
                  </p>
                </div>
              </div>
              <div className="settings-widget__pill-row">
                <span className="settings-widget__pill">Direct ZIP distribution</span>
                <span className="settings-widget__pill">Checksum verified</span>
                <span className="settings-widget__pill">Merchant-ready guide</span>
              </div>
            </div>

            {!hasWidgetMetadata && (
              <div className="settings-callout settings-callout--warning mb-3">
                Widget metadata could not be loaded from the API yet. Static fallback links are shown; update metadata before enabling downloads for merchants.
              </div>
            )}

            <div className="settings-widget__status-grid">
              <div className="settings-widget__status-card settings-widget__status-card--ready">
                <div className="settings-widget__status-label">Latest version</div>
                <div className="settings-widget__status-value">{widgetMetadata?.version || 'Unavailable'}</div>
                <div className="settings-widget__status-copy">Release package used for merchant installs.</div>
              </div>
              <div className="settings-widget__status-card">
                <div className="settings-widget__status-label">Release date</div>
                <div className="settings-widget__status-value">{formatTimestampLabel(widgetMetadata?.released_at || null)}</div>
                <div className="settings-widget__status-copy">UTC timestamp from the signed release metadata.</div>
              </div>
              <div className="settings-widget__status-card">
                <div className="settings-widget__status-label">Compatibility</div>
                <div className="settings-widget__status-value">Woo + WP Ready</div>
                <div className="settings-widget__status-copy">{compatibilityNotes}</div>
              </div>
            </div>

            <div className="settings-widget__quick-actions">
              <a
                href={downloadUrl || '#'}
                target="_blank"
                rel="noreferrer"
                className={`btn btn-primary ${downloadUrl ? '' : 'disabled'}`}
                aria-disabled={!downloadUrl}
              >
                Download Plugin (.zip)
              </a>
              <a
                href={docsUrl}
                target="_blank"
                rel="noreferrer"
                className="btn btn-outline-primary"
              >
                View Setup Guide
              </a>
              <a
                href={changelogUrl}
                target="_blank"
                rel="noreferrer"
                className="btn btn-outline-secondary"
              >
                View Changelog
              </a>
              <Button
                variant="outline-info"
                onClick={fetchWidgetMetadata}
                disabled={widgetLoading}
              >
                {widgetLoading ? 'Refreshing...' : 'Refresh metadata'}
              </Button>
            </div>
          </Card.Body>
        </Card>

        <Row className="g-4">
          <Col xl={7}>
            <Card className="operator-card">
              <Card.Header className="operator-card__header">
                <h5 className="operator-card__title">Release integrity</h5>
                <small className="operator-card__subtitle">Use these values when validating downloads in merchant support flows.</small>
              </Card.Header>
              <Card.Body>
                <Form.Group className="mb-3">
                  <Form.Label>Plugin slug</Form.Label>
                  <Form.Control
                    type="text"
                    value={widgetMetadata?.slug || 'rizzpay-for-woocommerce'}
                    readOnly
                    className="settings-code-field"
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Archive slug</Form.Label>
                  <Form.Control
                    type="text"
                    value={widgetMetadata?.archive_slug || 'rizzpay-woocommerce'}
                    readOnly
                    className="settings-code-field"
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>SHA256 checksum</Form.Label>
                  <Form.Control
                    type="text"
                    value={widgetMetadata?.sha256 || 'Unavailable'}
                    readOnly
                    className="settings-code-field"
                    style={{ '--code-color': '#7dd3a4' } as React.CSSProperties}
                  />
                </Form.Group>
              </Card.Body>
            </Card>
          </Col>
          <Col xl={5}>
            <Card className="operator-card">
              <Card.Header className="operator-card__header">
                <h5 className="operator-card__title">Merchant install path</h5>
                <small className="operator-card__subtitle">Share these steps exactly to reduce onboarding mistakes.</small>
              </Card.Header>
              <Card.Body>
                <div className="settings-guide-grid">
                  {installSteps.map((step, index) => (
                    <div key={step} className="settings-guide-card settings-guide-card--compact settings-widget__step-card">
                      <div className="settings-guide-card__step">Step {index + 1}</div>
                      <div className="settings-guide-card__copy">{step}</div>
                    </div>
                  ))}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    );
  };

  const renderReadOnlyFeeCard = (title: string, fee: number, note = 'Default') => (
    <Card className="operator-card mb-3">
      <Card.Body>
        <Card.Title className="operator-card__title">{title}</Card.Title>
        <div className="d-flex align-items-center">
          <span className="fs-4 fw-bold operator-accent-text">{fee}%</span>
          <span className="ms-2 operator-muted-text">({note})</span>
        </div>
      </Card.Body>
    </Card>
  );

  const renderEditableProcessingFeeCard = (flowType: FeeFlowType, fee: number) => {
    const isEditing = editingProcessingFlow === flowType;
    const draftValue = processingFeeDrafts[flowType];

    return (
      <Card className="operator-card mb-3">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-start gap-3">
            <div>
              <Card.Title className="operator-card__title">Platform Processing Fee</Card.Title>
              <div className="d-flex align-items-center flex-wrap gap-2">
                {isEditing ? (
                  <>
                    <Form.Control
                      type="number"
                      value={draftValue}
                      onChange={(e) => handleProcessingFeeDraftChange(flowType, e.target.value)}
                      min="0"
                      max="100"
                      step="0.01"
                      className="operator-control"
                      style={{ width: '120px' }}
                    />
                    <span className="operator-muted-text">%</span>
                  </>
                ) : (
                  <>
                    <span className="fs-4 fw-bold operator-accent-text">{fee}%</span>
                    <span className="operator-muted-text">(Applied before merchant distribution)</span>
                  </>
                )}
              </div>
              <small className="operator-muted-text d-block mt-2">
                This hidden platform fee is applied during settlement for {getFlowLabel(flowType).toLowerCase()} payments.
              </small>
            </div>
            <div className="d-flex gap-2 flex-wrap justify-content-end">
              {isEditing ? (
                <>
                  <Button size="sm" variant="success" onClick={() => handleSaveProcessingFee(flowType)}>
                    Save
                  </Button>
                  <Button size="sm" variant="outline-secondary" onClick={() => handleCancelProcessingFeeEdit(flowType, fee)}>
                    Cancel
                  </Button>
                </>
              ) : (
                <Button size="sm" variant="outline-primary" onClick={() => handleStartProcessingFeeEdit(flowType, fee)}>
                  Edit
                </Button>
              )}
            </div>
          </div>
        </Card.Body>
      </Card>
    );
  };

  const renderCustomFeesTable = (
    fees: EditableFee[],
    feeType: 'superAgent' | 'agent',
    flowType: FeeFlowType,
    title: string
  ) => (
    <Card className="operator-card mb-3">
      <Card.Header className="operator-card__header d-flex justify-content-between align-items-center">
        <h5 className="operator-card__title">{title}</h5>
        <Button
          variant="primary"
          size="sm"
          onClick={() => handleOpenAddModal(feeType === 'superAgent' ? 'SuperAgent' : 'Agent', flowType)}
        >
          Add Custom Fee
        </Button>
      </Card.Header>
      <Card.Body>
        {fees.length === 0 ? (
          <p className="settings-empty">No custom fees set for {getFlowLabel(flowType).toLowerCase()}</p>
        ) : (
          <Table responsive className="app-data-table settings-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Fee %</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {fees.map(fee => (
                <tr key={`${fee.userId}-${fee.feeType}-${fee.flowType}`}>
                  <td>{getUserName(fee.userId)}</td>
                  <td>
                    {fee.isEditing ? (
                      <div className="d-flex align-items-center">
                        <Form.Control
                          type="number"
                          value={fee.tempValue}
                          onChange={(e) => handleTempValueChange(fee.userId, feeType, flowType, e.target.value)}
                          min="0"
                          max="100"
                          step="0.01"
                          size="sm"
                          style={{ width: '100px' }}
                          className="me-2 operator-control"
                        />
                        <span className="me-2">%</span>
                      </div>
                    ) : (
                      <span className="fw-bold operator-accent-text">{fee.percent}%</span>
                    )}
                  </td>
                  <td>
                    {fee.isEditing ? (
                      <div>
                        <Button
                          variant="success"
                          size="sm"
                          onClick={() => handleSaveFee(fee.userId, feeType, flowType)}
                          className="me-2"
                        >
                          Save
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => handleCancelEdit(fee.userId, feeType, flowType)}
                        >
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleEditFee(fee.userId, feeType, flowType)}
                      >
                        Edit
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card.Body>
    </Card>
  );

  const settingsWorkspaces = [
    (userRole === 'Admin' || userRole === 'SuperAgent')
      ? {
          key: 'fee-customise',
          eyebrow: 'Processor controls',
          title: 'Fee rules',
          note: 'Direct crypto and fiat-onramp fees.',
          status: feeSettings?.flowSettings.length ? `${feeSettings.flowSettings.length} flows ready` : 'Review fees',
        }
      : null,
    {
      key: 'wallets',
      eyebrow: 'Payout setup',
      title: 'Wallets',
      note: 'Future payout destinations.',
      status: walletUsers.some(user => Boolean(user.settlementWallet?.trim()))
        ? `${walletUsers.filter(user => Boolean(user.settlementWallet?.trim())).length} ready`
        : 'Add wallet',
    },
    shouldShowTabs
      ? {
          key: 'onramper-flow',
          eyebrow: userRole === 'Admin' ? 'Risk controls' : 'Optional fiat',
          title: userRole === 'Admin' ? 'Onramper approvals' : 'Onramper flow',
          note: userRole === 'Admin'
            ? 'Review merchant requests and control provider access.'
            : 'Request card and bank flow access with Telegram verification.',
          status: userRole === 'Admin'
            ? (fiatOnrampQueueLoading ? 'Loading queue' : `${fiatOnrampQueue.length} in view`)
            : getFiatOnrampStatusLabel(
                fiatOnrampStatus?.fiatOnrampApprovalStatus ?? 'NotRequested',
                Boolean(fiatOnrampStatus?.fiatOnrampEnabled)
              ),
        }
      : null,
    (userRole === 'SuperAgent' || userRole === 'Agent')
      ? {
          key: 'api-access',
          eyebrow: 'Server integration',
          title: 'API Access',
          note: 'Server auth, webhooks, and docs.',
          status: merchantClientId ? 'Auth ready' : 'Add credentials',
        }
      : null,
    (userRole === 'SuperAgent' || userRole === 'Agent')
      ? {
          key: 'iframe',
          eyebrow: 'Hosted checkout',
          title: 'Iframe',
          note: 'One-page checkout inside the merchant site.',
          status: embedTokenName ? 'Embed ready' : 'Add embed token',
        }
      : null,
    canAccessWidgetWorkspace
      ? {
          key: 'widget',
          eyebrow: 'WooCommerce plugin',
          title: 'Widget',
          note: 'Download zip, checksum, and install docs.',
          status: widgetMetadata?.version ? `v${widgetMetadata.version}` : 'Load release metadata',
        }
      : null,
  ].filter(Boolean) as Array<{
    key: string;
    eyebrow: string;
    title: string;
    note: string;
    status: string;
  }>;

  if (loading && (userRole === 'Admin' || userRole === 'SuperAgent') && activeTab === 'fee-customise') {
    return (
      <div className="app-shell operator-page">
        <Container className="operator-page__container">
          <div className="d-flex justify-content-center">
            <Spinner animation="border" role="status" className="app-spinner">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        </Container>
      </div>
    );
  }

  if (error && !feeSettings && (userRole === 'Admin' || userRole === 'SuperAgent') && activeTab === 'fee-customise') {
    return (
      <div className="app-shell operator-page">
        <Container className="operator-page__container">
          <Alert variant="danger">
            {error}
            <Button variant="outline-light" className="ms-2" onClick={() => void fetchData()}>
              Retry
            </Button>
          </Alert>
        </Container>
      </div>
    );
  }

  return (
    <div className="app-shell operator-page">
      <Container className="operator-page__container">
        <Row>
          <Col>
            <InternalPageHeader
              eyebrow="Workspace controls"
              title="Settings"
              description="Manage fees, wallets, API access, webhooks, embedded checkout, and WooCommerce plugin distribution from one place."
              meta={[
                {
                  label: 'Role',
                  value: userRole === 'Admin' ? 'Processor' : getRoleDisplayLabel(userRole as 'Admin' | 'SuperAgent' | 'Agent'),
                },
                {
                  label: 'Access',
                  value: shouldShowTabs ? 'Role-based settings ready' : 'No editable sections',
                },
              ]}
            />
            <div className="settings-page__tab-shell mb-3">
              <div className="settings-page__workspace-intro">
                <div className="settings-page__workspace-intro-copy">
                  <span className="settings-page__workspace-intro-label">Choose a workspace</span>
                  <strong className="settings-page__workspace-intro-title">Open the area you want to work on right now</strong>
                  <span className="settings-page__workspace-intro-note">
                    Each workspace keeps its own setup steps and follow-up actions together.
                  </span>
                </div>
              </div>
              <div className="settings-page__tab-hint">Swipe to move between settings workspaces</div>
            </div>

             {error && (
              <Alert variant="danger" dismissible onClose={() => setError(null)}>
                 {error}
               </Alert>
             )}

             {success && (
              <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
                 {success}
               </Alert>
             )}

            {/* Tabs for Admin, SuperAgent, and Agent roles */}
             {shouldShowTabs ? (
                <div className="settings-page__layout">
                  <aside className="settings-page__sidebar">
                    <Nav
                      variant="tabs"
                      className="settings-page__tabs settings-page__workspace-nav"
                      activeKey={activeTab}
                      onSelect={(selectedKey) => handleTabSelect(selectedKey as string | null)}
                    >
                      {settingsWorkspaces.map(workspace => (
                        <Nav.Item key={workspace.key} className="settings-page__workspace-item">
                          <Nav.Link
                            eventKey={workspace.key}
                            className={`settings-page__workspace-link ${activeTab === workspace.key ? 'active' : ''}`}
                            onClick={(event) => {
                              event.preventDefault();
                              handleTabSelect(workspace.key);
                            }}
                          >
                            <span className="settings-page__workspace-eyebrow">{workspace.eyebrow}</span>
                            <span className="settings-page__workspace-title">{workspace.title}</span>
                            <span className="settings-page__workspace-note">{workspace.note}</span>
                            <span className="settings-page__workspace-status">{workspace.status}</span>
                          </Nav.Link>
                        </Nav.Item>
                      ))}
                    </Nav>
                  </aside>
                  <div className="settings-page__canvas">
                    {(userRole === 'Admin' || userRole === 'SuperAgent') && activeTab === 'fee-customise' && (
                      <div>{renderFeeCustomiseTab()}</div>
                    )}
                    {activeTab === 'wallets' && (
                      <div>{renderWalletsTab()}</div>
                    )}
                    {telegramFeatureEnabled && activeTab === 'telegram-ids' && (
                      <div>{renderTelegramIdsTab()}</div>
                    )}
                    {shouldShowTabs && activeTab === 'onramper-flow' && (
                      <div>{renderOnramperFlowTab()}</div>
                    )}
                    {(userRole === 'SuperAgent' || userRole === 'Agent') && activeTab === 'api-access' && (
                      <div>{renderApiAccessTab()}</div>
                    )}
                    {(userRole === 'SuperAgent' || userRole === 'Agent') && activeTab === 'iframe' && (
                      <div>{renderIframeTab()}</div>
                    )}
                    {canAccessWidgetWorkspace && activeTab === 'widget' && (
                      <div>{renderWidgetTab()}</div>
                    )}
                  </div>
                </div>
            ) : null}

            {userRole === 'Admin' && feeSettings && (
              <>
                <button
                  type="button"
                  className="settings-processing-fee-trigger"
                  onClick={handleOpenProcessingFeePanel}
                  aria-label="Open processing fee controls"
                >
                  PF
                </button>

                <Modal
                  show={showProcessingFeeModal}
                  onHide={handleCloseProcessingFeePanel}
                  centered
                  contentClassName="operator-modal settings-processing-fee-modal"
                >
                  <Modal.Header closeButton>
                    <Modal.Title>{processingFeeAccessGranted ? 'PF' : 'Unlock PF'}</Modal.Title>
                  </Modal.Header>
                  <Modal.Body>
                    {processingFeeAccessGranted ? (
                      <div className="settings-processing-fee-panel">
                        <p className="operator-muted-text mb-3">
                          Hidden platform processing fees for future direct crypto and fiat onramp settlements.
                        </p>
                        {FEE_FLOW_CONFIG.map(flow => {
                          const flowSettings = getFlowSettings(flow.key);
                          if (!flowSettings) {
                            return null;
                          }

                          return (
                            <div key={flow.key}>
                              {renderEditableProcessingFeeCard(flow.key, flowSettings.defaultProcessingPercent)}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <Form>
                        <Form.Group>
                          <Form.Label>Password</Form.Label>
                          <Form.Control
                            type="password"
                            value={processingFeePassword}
                            onChange={(e) => setProcessingFeePassword(e.target.value)}
                            disabled={processingFeeUnlocking}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleUnlockProcessingFeePanel();
                              }
                            }}
                            placeholder="Enter password"
                            autoFocus
                          />
                          {processingFeePasswordError && (
                            <div className="settings-processing-fee-error">{processingFeePasswordError}</div>
                          )}
                        </Form.Group>
                      </Form>
                    )}
                  </Modal.Body>
                  <Modal.Footer>
                    {processingFeeAccessGranted ? (
                      <>
                        <Button variant="outline-secondary" onClick={handleCloseProcessingFeePanel}>
                          Close
                        </Button>
                        <Button variant="secondary" onClick={handleLockProcessingFeePanel}>
                          Lock
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button variant="secondary" onClick={handleCloseProcessingFeePanel}>
                          Cancel
                        </Button>
                        <Button variant="primary" onClick={handleUnlockProcessingFeePanel} disabled={processingFeeUnlocking}>
                          {processingFeeUnlocking ? 'Unlocking...' : 'Unlock'}
                        </Button>
                      </>
                    )}
                  </Modal.Footer>
                </Modal>
              </>
            )}

            <Modal show={showWalletModal} onHide={handleCloseWalletModal} centered contentClassName="operator-modal">
              <Modal.Header closeButton>
                <Modal.Title>
                  Update Settlement Wallet
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <p className="operator-muted-text">
                  {selectedWalletUser
                    ? `This wallet will be used for future payment requests and invoices created for ${selectedWalletUser.name}.`
                    : 'Update the settlement wallet that future payment requests and invoices will use.'}
                </p>
                <Form.Group>
                  <Form.Label>Polygon Settlement Wallet Address</Form.Label>
                  <Form.Control
                    type="text"
                    value={walletAddress}
                    onChange={(e) => setWalletAddress(e.target.value)}
                    placeholder="Enter ERC-20 wallet address (0x...)"
                  />
                  <Form.Text>
                    Enter a valid Polygon-compatible ERC-20 address. Existing unpaid payment requests keep the wallet that was already assigned when they were created.
                  </Form.Text>
                </Form.Group>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={handleCloseWalletModal} disabled={walletSaving}>
                  Cancel
                </Button>
                <Button variant="primary" onClick={handleUpdateWallet} disabled={walletSaving}>
                  {walletSaving ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      Updating...
                    </>
                  ) : (
                    'Save Wallet'
                  )}
                </Button>
              </Modal.Footer>
            </Modal>

            {/* Add Custom Fee Modal */}
            <Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered contentClassName="operator-modal">
              <Modal.Header closeButton>
                <Modal.Title>Add Custom Fee</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <Form>
                  <Form.Group className="mb-3">
                    <Form.Label>Payment Flow</Form.Label>
                    <Form.Control
                      type="text"
                      value={getFlowLabel(newFee.flowType)}
                      readOnly
                      className="settings-code-field"
                      style={{ cursor: 'not-allowed' }}
                    />
                    <Form.Text>
                      {getFlowDescription(newFee.flowType)}
                    </Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Fee Type</Form.Label>
                    <Form.Control
                      type="text"
                      value={getAddFeeTypeDisplayLabel(newFee.feeType)}
                      readOnly
                      className="settings-code-field"
                      style={{ cursor: 'not-allowed' }}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>User</Form.Label>
                    <Form.Select
                      value={newFee.userId}
                      onChange={(e) => setNewFee({ ...newFee, userId: parseInt(e.target.value) })}
                    >
                      <option value={0}>
                        {users.filter(user => user.role === newFee.feeType).length === 0
                          ? `No ${getAddFeeTargetLabel(newFee.feeType)} users available`
                          : `Select a ${getAddFeeTargetLabel(newFee.feeType)}...`}
                      </option>
                      {users
                        .filter(user => user.role === newFee.feeType)
                        .map(user => (
                          <option key={user.id} value={user.id}>
                            {user.name} ({user.email})
                          </option>
                        ))}
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Percentage</Form.Label>
                    <Form.Control
                      type="number"
                      value={newFee.percent || ''}
                      onChange={(e) => setNewFee({ ...newFee, percent: parseFloat(e.target.value) || 0 })}
                      min="0"
                      max="100"
                      step="0.01"
                      placeholder="Enter percentage"
                    />
                  </Form.Group>
                </Form>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={() => setShowAddModal(false)}>
                  Cancel
                </Button>
                <Button variant="primary" onClick={handleAddCustomFee}>
                  Add Fee
                </Button>
              </Modal.Footer>
            </Modal>

            {telegramFeatureEnabled && (
              <Modal show={showTelegramModal} onHide={() => setShowTelegramModal(false)} centered contentClassName="operator-modal">
                <Modal.Header closeButton>
                  <Modal.Title>Add Telegram Account</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  <Form>
                    <Form.Group className="mb-3">
                      <Form.Label>Telegram ID</Form.Label>
                      <Form.Control
                        type="text"
                        value={newTelegramId}
                        onChange={(e) => setNewTelegramId(e.target.value)}
                        placeholder="Enter Telegram ID (numeric)"
                      />
                      <Form.Text>
                        Enter the numeric Telegram ID. Use the "Get Telegram ID" button to find your ID from a username.
                      </Form.Text>
                    </Form.Group>
                  </Form>
                </Modal.Body>
                <Modal.Footer>
                  <Button variant="secondary" onClick={() => setShowTelegramModal(false)}>
                    Cancel
                  </Button>
                  <Button variant="primary" onClick={handleAddTelegramAccount}>
                    Add Account
                  </Button>
                </Modal.Footer>
              </Modal>
            )}
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Settings;
